import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { getAuth, isSuperAdmin } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Save, Settings as SettingsIcon, Building2, Users, Trash2, UserPlus, Mail, Copy, Inbox, Clock, Power, Zap, CheckCircle2, Check, AlertTriangle, Info, HelpCircle, ExternalLink, Book, Eye, ShieldCheck, Monitor, Smartphone, MailWarning, Server, KeyRound, EyeOff, RefreshCw, PlugZap, CreditCard, CalendarClock, FileCog, Upload, FileSpreadsheet, Landmark, Download, TrendingDown, TrendingUp, Hash, Plus, Pencil, X } from 'lucide-react';
import SubscriptionSettings from '../components/settings/SubscriptionSettings';
import BillingSettings from '../components/settings/BillingSettings';
import { AstridApMapper } from '../components/settings/AstridApMapper';
import EnergyStarTab from '../components/settings/EnergyStarTab';

export const Settings = () => {
  const navigate = useNavigate();
  const { user, accounts } = getAuth();
  const currentAccountStr = localStorage.getItem('current_account');
  
  // Auto-select first account if none selected
  let currentAccount = currentAccountStr ? JSON.parse(currentAccountStr) : null;
  if (!currentAccount && accounts && accounts.length > 0) {
    currentAccount = accounts[0];
    localStorage.setItem('current_account', JSON.stringify(currentAccount));
  }
  
  const [activeTab, setActiveTab] = useState('organisation');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Organisation settings
  const [orgSettings, setOrgSettings] = useState({
    organisation_name: '',
    account_no: '',
  });
  
  // Integration settings
  const [integrationSettings, setIntegrationSettings] = useState({
    enable_billportal_format: false,
  });
  
  // API Key management
  const [apiKeyStatus, setApiKeyStatus] = useState({ hasKey: false, isActive: false });
  const [newApiKey, setNewApiKey] = useState(null);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [showApiDocsModal, setShowApiDocsModal] = useState(false);
  
  // User management
  const [users, setUsers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'viewer' });
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  
  // Inbox settings
  const [inboxSettings, setInboxSettings] = useState(null);

  // Missing Bills settings (org-level Missing Bill Start Date)
  const [mbSettings, setMbSettings] = useState(null);
  const [mbStart, setMbStart] = useState('');
  const [mbComputeUnits, setMbComputeUnits] = useState(true);
  const [mbUnitsSaving, setMbUnitsSaving] = useState(false);
  const [mbDisableUnitsOpen, setMbDisableUnitsOpen] = useState(false);
  const [mbSaving, setMbSaving] = useState(false);
  // Late-Arriving → Missing threshold (days) config
  const [mbLateDays, setMbLateDays] = useState('');
  const [mbLateSaving, setMbLateSaving] = useState(false);
  // Live "X → Y bills" impact preview as the user edits the Start Date (debounced).
  const [mbPreview, setMbPreview] = useState(null);
  const [mbPreviewLoading, setMbPreviewLoading] = useState(false);
  
  // Parser Config settings (simplified - validation deprecated)
  const [parserConfig, setParserConfig] = useState({
    parsing_mode: 'hybrid',
    ocr_engine: 3,
  });
  const [loadingParserConfig, setLoadingParserConfig] = useState(false);

  const isAdmin = currentAccount?.role === 'admin' || user?.is_super_admin;

  useEffect(() => {
    if (!currentAccount) {
      toast.error('No account selected');
      navigate('/dashboard');
      return;
    }
    fetchUsers();
    fetchOrgDetails();
    fetchInboxSettings();
    fetchIntegrationSettings();
    fetchApiKeyStatus();
    fetchParserConfig();
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount?.id, navigate]);

  const fetchOrgDetails = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/details`);
      setOrgSettings({
        organisation_name: data.organisation_name || data.company_name || '',
        account_no: data.account_no || '',
      });
    } catch (error) {
      console.error('Failed to load org details');
    }
  };

  const fetchUsers = async () => {
    if (!currentAccount || !isAdmin) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/users`);
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const fetchInboxSettings = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/inbox`);
      setInboxSettings(data);
    } catch (error) {
      console.error('Failed to load inbox settings');
    }
  };

  const fetchIntegrationSettings = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/settings`);
      setIntegrationSettings({
        enable_billportal_format: data.enable_billportal_format || false,
      });
    } catch (error) {
      console.error('Failed to load integration settings');
    }
  };

  const fetchParserConfig = async () => {
    if (!currentAccount || !isAdmin) return;
    setLoadingParserConfig(true);
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/parsing-config`);
      setParserConfig({
        parsing_mode: data.parsing_mode || 'hybrid',
        ocr_engine: data.ocr_engine || 3,
      });
    } catch (error) {
      console.error('Failed to load parser config');
    } finally {
      setLoadingParserConfig(false);
    }
  };

  const handleSaveParserConfig = async () => {
    if (!currentAccount || !isAdmin) return;
    setSaving(true);
    try {
      await api.put(`/accounts/${currentAccount.id}/parsing-config`, {
        ...parserConfig,
        parsing_mode: 'hybrid',
        ocr_enabled: true,
        ocr_provider: 'ocr_space',
        llm_provider: 'openai',
        llm_model: 'gpt-5.2',
        llm_api_key_source: 'global',
      });
      toast.success('Parser configuration saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save parser config');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveIntegrationSettings = async () => {
    if (!currentAccount || !isAdmin) return;
    setSaving(true);
    try {
      await api.put(`/accounts/${currentAccount.id}/settings`, {
        ai_provider: 'emergent',
        model_name: 'gpt-5.2',
        enable_billportal_format: integrationSettings.enable_billportal_format,
      });
      toast.success('Integration settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const fetchApiKeyStatus = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/details`);
      setApiKeyStatus({
        hasKey: !!data.api_key,
        isActive: data.api_key_active || false,
        createdAt: data.api_key_created_at
      });
    } catch (error) {
      console.error('Failed to load API key status');
    }
  };

  const handleGenerateApiKey = async () => {
    if (!currentAccount || !isAdmin) return;
    if (!window.confirm('This will generate a new API key. Any existing key will be replaced. Continue?')) return;
    
    setGeneratingKey(true);
    try {
      const { data } = await api.post(`/accounts/${currentAccount.id}/generate-api-key`);
      setNewApiKey(data.api_key);
      setApiKeyStatus({ hasKey: true, isActive: true, createdAt: new Date().toISOString() });
      toast.success('API key generated! Copy it now - it won\'t be shown again.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate API key');
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleRevokeApiKey = async () => {
    if (!currentAccount || !isAdmin) return;
    if (!window.confirm('This will revoke the API key. External integrations will stop working. Continue?')) return;
    
    try {
      await api.delete(`/accounts/${currentAccount.id}/revoke-api-key`);
      setApiKeyStatus({ hasKey: true, isActive: false });
      setNewApiKey(null);
      toast.success('API key revoked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleSaveOrg = async () => {
    if (!currentAccount || !isAdmin) return;
    setSaving(true);
    try {
      await api.put(`/accounts/${currentAccount.id}/details`, orgSettings);
      // Update local storage with new name
      const updatedAccount = { ...currentAccount, organisation_name: orgSettings.organisation_name };
      localStorage.setItem('current_account', JSON.stringify(updatedAccount));
      toast.success('Organisation details saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/accounts/${currentAccount.id}/users/invite`, inviteForm);
      toast.success(`Invited ${inviteForm.email}`);
      setInviteForm({ email: '', name: '', role: 'viewer' });
      setShowInvite(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api.put(`/accounts/${currentAccount.id}/users/${userId}/role`, { role: newRole });
      toast.success('Role updated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user from the organization?')) return;
    try {
      await api.delete(`/accounts/${currentAccount.id}/users/${userId}`);
      toast.success('User removed');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  const handleUpdateUserStatus = async (userId, newStatus) => {
    const action = newStatus === 'active' ? 'activate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    try {
      await api.patch(`/accounts/${currentAccount.id}/users/${userId}/status`, { status: newStatus });
      toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleTogglePfReviewer = async (userId, enabled) => {
    // Optimistic update
    setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, pf_reviewer: enabled } : u));
    try {
      await api.patch(`/accounts/${currentAccount.id}/users/${userId}/pf-reviewer`, { pf_reviewer: enabled });
      toast.success(enabled ? 'PF Reviewer enabled' : 'PF Reviewer disabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update PF reviewer flag');
      fetchUsers();
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  // Parser Config only matters when the intake-hub feature is enabled on
  // the org. Otherwise the tab adds clutter for orgs that don't use it.
  const isIntakeHubEnabled = currentAccount?.enabled_apps?.intake_hub === true;
  const isGlConfigEnabled = isIntakeHubEnabled
    || currentAccount?.enabled_apps?.ubm === true
    || currentAccount?.enabled_apps?.payments === true;

  // Load Missing Bills settings when that tab is opened.
  useEffect(() => {
    if (activeTab !== 'missing-bills' || !currentAccount?.id) return;
    api.get('/intake/missing-bills/settings', { params: { account_id: currentAccount.id } })
      .then(({ data }) => { setMbSettings(data); setMbStart(data.missing_bill_start_date || ''); setMbComputeUnits(data.compute_unit_bills !== false); setMbLateDays(data.late_to_missing_days != null ? String(data.late_to_missing_days) : ''); })
      .catch(() => toast.error('Failed to load Missing Bills settings'));
  }, [activeTab, currentAccount?.id]);

  // Debounced dry-run preview: only fires when the edited value differs from
  // what's saved. Shows how the active Missing Bills count would change.
  useEffect(() => {
    if (activeTab !== 'missing-bills' || !currentAccount?.id || !mbSettings) return;
    const savedVal = mbSettings.missing_bill_start_date || '';
    if (mbStart === savedVal) { setMbPreview(null); setMbPreviewLoading(false); return; }
    setMbPreviewLoading(true);
    const t = setTimeout(() => {
      api.post('/intake/missing-bills/settings/preview',
        { missing_bill_start_date: mbStart || null },
        { params: { account_id: currentAccount.id } })
        .then(({ data }) => setMbPreview(data))
        .catch(() => setMbPreview(null))
        .finally(() => setMbPreviewLoading(false));
    }, 600);
    return () => clearTimeout(t);
  }, [mbStart, activeTab, currentAccount?.id, mbSettings]);

  const saveMissingBills = async () => {
    setMbSaving(true);
    try {
      const { data } = await api.put('/intake/missing-bills/settings',
        { missing_bill_start_date: mbStart || null },
        { params: { account_id: currentAccount.id } });
      setMbSettings(data);
      setMbStart(data.missing_bill_start_date || '');
      const applied = data.recompute_summary?.periods_upserted;
      toast.success(
        data.recomputed
          ? `Missing Bill Start Date saved and applied${typeof applied === 'number' ? ` (${applied} periods recomputed)` : ''}.`
          : 'Missing Bill Start Date saved.'
      );
    } catch {
      toast.error('Failed to save');
    } finally {
      setMbSaving(false);
    }
  };

  // Unit-tracking config persists immediately because turning it off deletes
  // the computed unit missing bills.
  const applyComputeUnits = async (value) => {
    setMbUnitsSaving(true);
    try {
      const { data } = await api.put('/intake/missing-bills/settings',
        { compute_unit_bills: value },
        { params: { account_id: currentAccount.id } });
      setMbSettings(data);
      setMbComputeUnits(data.compute_unit_bills !== false);
      toast.success(value
        ? 'Unit bill tracking enabled and applied.'
        : 'Unit bill tracking disabled. Computed unit missing bills deleted.');
    } catch {
      toast.error('Failed to update unit tracking');
    } finally {
      setMbUnitsSaving(false);
      setMbDisableUnitsOpen(false);
    }
  };

  const onToggleComputeUnits = (checked) => {
    if (checked) {
      applyComputeUnits(true);
    } else {
      setMbDisableUnitsOpen(true);  // confirm before deleting unit bills
    }
  };

  // Late-Arriving → Missing threshold: how many days a bill stays "Late
  // Arriving" before it flips to "Missing". Persists + recomputes immediately.
  const saveLateThreshold = async () => {
    setMbLateSaving(true);
    try {
      const parsed = parseInt(mbLateDays, 10);
      const val = (mbLateDays === '' || isNaN(parsed) || parsed < 1) ? null : parsed;
      const { data } = await api.put('/intake/missing-bills/settings',
        { missing_bill_late_to_missing_days: val },
        { params: { account_id: currentAccount.id } });
      setMbSettings(data);
      setMbLateDays(data.late_to_missing_days != null ? String(data.late_to_missing_days) : '');
      toast.success(data.late_to_missing_days
        ? `Late-arriving \u2192 Missing set to ${data.late_to_missing_days} day(s) and applied.`
        : 'Threshold cleared \u2014 using the default (newest overdue bill stays Late Arriving).');
    } catch {
      toast.error('Failed to save threshold');
    } finally {
      setMbLateSaving(false);
    }
  };

  const tabs = [
    { id: 'organisation', label: 'Organisation', icon: Building2, adminOnly: false },
    { id: 'subscription', label: 'Billing & Subscription', icon: CreditCard, adminOnly: false },
    ...(isIntakeHubEnabled ? [{ id: 'missing-bills', label: 'Missing Bills', icon: MailWarning, adminOnly: true }] : []),
    ...(isGlConfigEnabled ? [{ id: 'gl-code-config', label: 'GL Code Configuration', icon: Hash, adminOnly: true }] : []),
    ...(isIntakeHubEnabled ? [{ id: 'payments-schedule', label: 'Payment File Configuration', icon: FileCog, adminOnly: true }] : []),
    { id: 'integrations', label: 'Integrations', icon: Power, adminOnly: true },
    { id: 'energy-star', label: 'Energy Star', icon: PlugZap, adminOnly: true },
    { id: 'users', label: 'Users', icon: Users, adminOnly: true },
    { id: 'feature-visibility', label: 'Feature Visibility', icon: Eye, adminOnly: true },
    { id: 'inbox', label: 'Email Inbox', icon: Inbox, adminOnly: false },
    { id: 'trusted-devices', label: 'Trusted Devices', icon: ShieldCheck, adminOnly: false },
    { id: 'sftp', label: 'SFTP', icon: Server, adminOnly: true },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-dashboard"
              variant="ghost"
              size="sm"
              className="rounded-none"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <SettingsIcon className="h-6 w-6" strokeWidth={2} />
              <h1 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Settings
              </h1>
            </div>
          </div>
          <div
            className="text-sm text-muted-foreground max-w-[16rem] md:max-w-[24rem] truncate"
            title={orgSettings.organisation_name || currentAccount?.organisation_name || ''}
            data-testid="settings-org-name"
          >
            {orgSettings.organisation_name || currentAccount?.organisation_name}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto max-w-[1800px] px-6 md:px-12 py-8">
        <div className="flex gap-8">
          {/* Vertical Tab Menu */}
          <div className="w-56 shrink-0">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                if (tab.adminOnly && !isAdmin) return null;
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-none transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className={`flex-1 min-w-0 ${activeTab === 'users' || activeTab === 'feature-visibility' || activeTab === 'inbox' || activeTab === 'subscription' || activeTab === 'energy-star' ? 'max-w-none' : 'max-w-3xl'}`}>
            {/* Billing & Subscription Tab */}
            {activeTab === 'subscription' && (
              <BillingSettings
                currentAccount={currentAccount}
                initialSub={new URLSearchParams(window.location.search).get('sub') || 'bill_subscription'}
              />
            )}

            {activeTab === 'energy-star' && isAdmin && (
              <EnergyStarTab currentAccount={currentAccount} />
            )}
            {/* GL Code Configuration Tab (global commodity-level GL for payment files) */}
            {activeTab === 'gl-code-config' && isAdmin && (
              <GlCodeConfigTab currentAccount={currentAccount} />
            )}
            {/* Payment File Configuration Tab (Payment Schedule + AP File Format) */}
            {activeTab === 'payments-schedule' && isAdmin && (
              <PaymentFileConfigTab />
            )}
            {/* Organisation Tab */}
            {activeTab === 'organisation' && (
              <div className="border border-border rounded-none p-8 bg-card">
                <h2 className="text-2xl font-bold tracking-tight mb-6">Organisation Details</h2>
                <div className="space-y-6">
                  <div>
                    <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">
                      Organisation Name
                    </Label>
                    <Input
                      className="rounded-none"
                      value={orgSettings.organisation_name}
                      onChange={(e) => setOrgSettings({ ...orgSettings, organisation_name: e.target.value })}
                      disabled={!isAdmin}
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">
                      Account Number
                    </Label>
                    <Input
                      className="rounded-none font-mono"
                      value={orgSettings.account_no}
                      disabled
                    />
                    <p className="text-xs text-muted-foreground mt-1">Account numbers are system-generated</p>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">
                      Organisation ID
                    </Label>
                    <Input
                      className="rounded-none font-mono bg-muted"
                      value={currentAccount?.id || ''}
                      disabled
                    />
                    <p className="text-xs text-muted-foreground mt-1">System-generated unique identifier</p>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">
                      Your Role
                    </Label>
                    <Input
                      className="rounded-none capitalize bg-muted"
                      value={currentAccount?.role || ''}
                      disabled
                    />
                  </div>
                  {isAdmin && (
                    <Button
                      className="rounded-none font-bold uppercase"
                      onClick={handleSaveOrg}
                      disabled={saving}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  )}
                  {!isAdmin && (
                    <p className="text-sm text-muted-foreground">Only admins can edit organisation details.</p>
                  )}
                </div>
              </div>
            )}

            {/* Missing Bills Tab */}
            {activeTab === 'missing-bills' && isAdmin && (
              <div className="border border-border rounded-none p-8 bg-card" data-testid="missing-bills-settings">
                <h2 className="text-2xl font-bold tracking-tight mb-2 flex items-center gap-2">
                  <MailWarning className="h-6 w-6" /> Missing Bills
                </h2>
                <p className="text-muted-foreground mb-6">
                  Set the date from which missing bills are tracked for this organisation.
                  Periods before this date are treated as historical and excluded from the
                  client-facing Missing Bills counts (so older gaps don't alarm the client).
                </p>
                <div className="space-y-6 max-w-md">
                  <div>
                    <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">
                      Missing Bill Start Date
                    </Label>
                    <Input
                      type="date"
                      className="rounded-none"
                      value={mbStart}
                      onChange={(e) => setMbStart(e.target.value)}
                      data-testid="mb-start-date-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {mbSettings?.default_start_date
                        ? `Defaults to your organisation's creation date (${mbSettings.default_start_date}). Leave blank to use the default.`
                        : 'Leave blank to use your organisation\u2019s creation date.'}
                    </p>
                    {mbSettings?.effective_start_date && (
                      <p className="text-xs mt-2">
                        Currently counting from{' '}
                        <span className="font-semibold">{mbSettings.effective_start_date}</span>
                        {mbSettings.missing_bill_start_date ? ' (custom)' : ' (default — org creation date)'}.
                      </p>
                    )}
                  </div>
                  {(mbPreviewLoading || (mbPreview && !mbPreview.unchanged)) && (
                    <div
                      className="max-w-md border rounded-none px-3 py-2.5 text-sm flex items-start gap-2 bg-muted/40 border-border"
                      data-testid="mb-start-date-preview"
                    >
                      {mbPreviewLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin mt-0.5 shrink-0 text-muted-foreground" />
                          <span className="text-muted-foreground">Calculating impact…</span>
                        </>
                      ) : (
                        <>
                          {mbPreview.delta < 0
                            ? <TrendingDown className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                            : mbPreview.delta > 0
                              ? <TrendingUp className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
                              : <Info className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />}
                          <span data-testid="mb-start-date-preview-text">
                            Saving will change Missing Bills from{' '}
                            <span className="font-semibold">{mbPreview.current_total.toLocaleString()}</span>
                            {' \u2192 '}
                            <span className="font-semibold">{mbPreview.new_total.toLocaleString()}</span>
                            {' '}
                            <span className={mbPreview.delta < 0 ? 'text-amber-600 font-medium' : mbPreview.delta > 0 ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}>
                              ({mbPreview.delta > 0 ? '+' : ''}{mbPreview.delta.toLocaleString()})
                            </span>
                            <span className="block text-xs text-muted-foreground mt-0.5">
                              Counting from {mbPreview.new_start_date}{mbPreview.is_default ? ' (default)' : ''}.
                            </span>
                          </span>
                        </>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Button
                      className="rounded-none font-bold uppercase"
                      onClick={saveMissingBills}
                      disabled={mbSaving}
                      data-testid="mb-start-date-save"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {mbSaving ? 'Saving...' : 'Save'}
                    </Button>
                    {mbStart && (
                      <Button
                        variant="outline"
                        className="rounded-none"
                        onClick={() => { setMbStart(''); }}
                        data-testid="mb-start-date-clear"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
                <div className="border-t border-border mt-8 pt-6 max-w-md">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="mb-compute-units-setting">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={mbComputeUnits}
                      disabled={mbUnitsSaving}
                      onChange={(e) => onToggleComputeUnits(e.target.checked)}
                    />
                    <span>
                      <span className="font-medium">Compute missing bills for units</span>
                      <span className="block text-xs text-muted-foreground mt-1">
                        When enabled, unit-level missing bills are tracked from your
                        Missing Bill Start Date and counted in the stats. When disabled,
                        only Common Area bills are tracked and existing unit missing
                        bills are deleted.
                      </span>
                    </span>
                  </label>
                </div>
                <div className="border-t border-border mt-8 pt-6 max-w-md">
                  <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">
                    Late-Arriving → Missing after (days)
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      className="rounded-none w-32"
                      placeholder="e.g. 4"
                      value={mbLateDays}
                      onChange={(e) => setMbLateDays(e.target.value)}
                      data-testid="mb-late-days-input"
                    />
                    <Button
                      className="rounded-none font-bold uppercase"
                      onClick={saveLateThreshold}
                      disabled={mbLateSaving}
                      data-testid="mb-late-days-save"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {mbLateSaving ? 'Saving...' : 'Save'}
                    </Button>
                    {mbLateDays !== '' && (
                      <Button
                        variant="outline"
                        className="rounded-none"
                        onClick={() => setMbLateDays('')}
                        data-testid="mb-late-days-clear"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    A bill stays <span className="text-amber-700 font-medium">Late Arriving</span> for
                    this many days after its expected date, then flips to{' '}
                    <span className="text-rose-700 font-medium">Missing (overdue)</span>. Example: a bill
                    expected 06 Aug with a 4-day threshold shows as Missing on 10 Aug. Leave blank to use
                    the default (only the most recent overdue bill stays Late Arriving).
                  </p>
                </div>
                <AlertDialog open={mbDisableUnitsOpen} onOpenChange={setMbDisableUnitsOpen}>
                  <AlertDialogContent className="rounded-none" data-testid="mb-disable-units-confirm">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Turn off unit missing bills?</AlertDialogTitle>
                      <AlertDialogDescription>
                        The missing bills already computed for units will be permanently
                        deleted, and only Common Area bills will be tracked going forward.
                        Are you sure?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-none" data-testid="mb-disable-units-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="rounded-none bg-red-600 hover:bg-red-700"
                        onClick={() => applyComputeUnits(false)}
                        data-testid="mb-disable-units-confirm-btn"
                      >
                        Yes, turn off & delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}

            {/* Integrations Tab */}
            {activeTab === 'integrations' && isAdmin && (
              <div className="border border-border rounded-none p-8 bg-card">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold tracking-tight">Integrations</h2>
                  <Dialog open={showApiDocsModal} onOpenChange={setShowApiDocsModal}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-none gap-2" data-testid="api-docs-help-btn">
                        <Book className="h-4 w-4" />
                        API Documentation
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto rounded-none">
                      <DialogHeader>
                        <DialogTitle className="text-xl font-bold flex items-center gap-2">
                          <Book className="h-5 w-5" />
                          BillPortal External API Documentation
                        </DialogTitle>
                      </DialogHeader>
                      
                      <div className="space-y-6 mt-4">
                        {/* Authentication */}
                        <div className="border border-border rounded-none p-4">
                          <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                            <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">1</span>
                            Authentication
                          </h3>
                          <p className="text-sm text-muted-foreground mb-3">
                            All API requests require an API key passed via the <code className="bg-muted px-1.5 py-0.5 rounded text-xs">X-API-Key</code> header.
                          </p>
                          <div className="bg-muted/50 p-3 rounded-none">
                            <code className="text-xs font-mono">X-API-Key: your_api_key_here</code>
                          </div>
                        </div>

                        {/* Upload APIs */}
                        <div className="border border-border rounded-none p-4">
                          <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                            <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                            Upload & Process Bills
                          </h3>
                          
                          <div className="space-y-4">
                            <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded-none border-l-4 border-blue-500">
                              <p className="font-mono text-sm font-bold text-blue-700 dark:text-blue-400">POST /api/external/v1/documents/upload</p>
                              <p className="text-xs text-muted-foreground mt-1">Upload a PDF bill for processing. Returns document ID for tracking.</p>
                              <div className="mt-2 space-y-1 text-xs">
                                <p><span className="font-semibold">Form Data:</span></p>
                                <ul className="list-disc list-inside ml-2 space-y-0.5">
                                  <li><code className="bg-muted px-1">file</code> - PDF file (required)</li>
                                  <li><code className="bg-muted px-1">external_id</code> - Your reference ID (optional, must be unique)</li>
                                  <li><code className="bg-muted px-1">reprocess</code> - Set to "true" to replace existing doc with same external_id</li>
                                </ul>
                              </div>
                              <div className="mt-3 bg-slate-900 text-slate-100 p-2 rounded text-[10px] font-mono overflow-x-auto">
                                <pre>{`curl -X POST "${window.location.origin}/api/external/v1/documents/upload" \\
  -H "X-API-Key: your_api_key" \\
  -F "file=@/path/to/bill.pdf" \\
  -F "external_id=YOUR-TRACKING-ID-123"`}</pre>
                              </div>
                            </div>

                            <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded-none border-l-4 border-green-500">
                              <p className="font-mono text-sm font-bold text-green-700 dark:text-green-400">GET /api/external/v1/documents/by-external-id/{'{external_id}'}</p>
                              <p className="text-xs text-muted-foreground mt-1">Get document status and parsed data by your external reference ID.</p>
                              <div className="mt-2 space-y-1 text-xs">
                                <p><span className="font-semibold">Query Params:</span></p>
                                <ul className="list-disc list-inside ml-2 space-y-0.5">
                                  <li><code className="bg-muted px-1">include_parsed_data</code> - Include extracted data (default: true)</li>
                                  <li><code className="bg-muted px-1">include_pdf_url</code> - Include presigned PDF URL (default: false)</li>
                                </ul>
                              </div>
                            </div>

                            <div className="bg-slate-50 dark:bg-slate-950/30 p-3 rounded-none border-l-4 border-slate-500">
                              <p className="font-mono text-sm font-bold">GET /api/external/v1/documents/{'{document_id}'}/status</p>
                              <p className="text-xs text-muted-foreground mt-1">Poll for document processing status.</p>
                              <p className="text-xs mt-2"><span className="font-semibold">Status values:</span> <code className="bg-muted px-1">uploaded</code>, <code className="bg-muted px-1">processing</code>, <code className="bg-muted px-1">completed</code>, <code className="bg-muted px-1">failed</code></p>
                            </div>
                          </div>
                        </div>

                        {/* BillPortal Format */}
                        <div className="border border-border rounded-none p-4">
                          <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                            <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                            BillPortal Format Export
                          </h3>
                          
                          <div className="space-y-4">
                            <div className="bg-purple-50 dark:bg-purple-950/30 p-3 rounded-none border-l-4 border-purple-500">
                              <p className="font-mono text-sm font-bold text-purple-700 dark:text-purple-400">POST /api/external/v1/documents/by-external-id/{'{external_id}'}/convert-billportal</p>
                              <p className="text-xs text-muted-foreground mt-1">Convert a completed document to BillPortal JSON format.</p>
                              <p className="text-xs mt-2 text-amber-600 dark:text-amber-400">⚠️ Document must be in 'completed' status.</p>
                            </div>

                            <div className="bg-purple-50 dark:bg-purple-950/30 p-3 rounded-none border-l-4 border-purple-500">
                              <p className="font-mono text-sm font-bold text-purple-700 dark:text-purple-400">POST /api/external/v1/documents/bulk-convert-billportal</p>
                              <p className="text-xs text-muted-foreground mt-1">Bulk convert all completed documents that haven't been converted yet.</p>
                            </div>

                            <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded-none border-l-4 border-green-500">
                              <p className="font-mono text-sm font-bold text-green-700 dark:text-green-400">GET /api/external/v1/exports</p>
                              <p className="text-xs text-muted-foreground mt-1">List all BillPortal format exports.</p>
                              <div className="mt-2 space-y-1 text-xs">
                                <p><span className="font-semibold">Query Params:</span></p>
                                <ul className="list-disc list-inside ml-2 space-y-0.5">
                                  <li><code className="bg-muted px-1">pulled</code> - Filter by pulled status (true/false)</li>
                                  <li><code className="bg-muted px-1">limit</code> - Max records (default: 100)</li>
                                </ul>
                              </div>
                            </div>

                            <div className="bg-slate-50 dark:bg-slate-950/30 p-3 rounded-none border-l-4 border-slate-500">
                              <p className="font-mono text-sm font-bold">POST /api/external/v1/exports/mark-pulled</p>
                              <p className="text-xs text-muted-foreground mt-1">Mark exports as pulled after syncing to your system.</p>
                              <div className="mt-2 text-xs">
                                <p><span className="font-semibold">Body:</span> <code className="bg-muted px-1">{`{"bill_track_ids": ["id1", "id2"]}`}</code></p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Reprocess APIs */}
                        <div className="border border-border rounded-none p-4">
                          <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                            <span className="w-6 h-6 bg-orange-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                            Reprocess Documents
                          </h3>
                          
                          <div className="space-y-4">
                            <div className="bg-orange-50 dark:bg-orange-950/30 p-3 rounded-none border-l-4 border-orange-500">
                              <p className="font-mono text-sm font-bold text-orange-700 dark:text-orange-400">POST /api/external/v1/documents/by-external-id/{'{external_id}'}/reprocess</p>
                              <p className="text-xs text-muted-foreground mt-1">Reprocess a document that is stuck or failed. Useful for re-extracting data.</p>
                            </div>

                            <div className="bg-orange-50 dark:bg-orange-950/30 p-3 rounded-none border-l-4 border-orange-500">
                              <p className="font-mono text-sm font-bold text-orange-700 dark:text-orange-400">POST /api/external/v1/documents/reprocess/{'{external_id}'}</p>
                              <p className="text-xs text-muted-foreground mt-1">Alternative endpoint: Reprocess by external_id (in URL path).</p>
                              <div className="mt-2 text-xs">
                                <p><span className="font-semibold">Body (optional):</span> <code className="bg-muted px-1">{`{"mode": "full"}`}</code> or <code className="bg-muted px-1">{`{"mode": "ocr_only"}`}</code></p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Webhooks */}
                        <div className="border border-border rounded-none p-4">
                          <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                            <span className="w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs font-bold">5</span>
                            Webhooks (Optional)
                          </h3>
                          
                          <div className="space-y-4">
                            <div className="bg-teal-50 dark:bg-teal-950/30 p-3 rounded-none border-l-4 border-teal-500">
                              <p className="font-mono text-sm font-bold text-teal-700 dark:text-teal-400">PUT /api/external/v1/webhooks</p>
                              <p className="text-xs text-muted-foreground mt-1">Configure a webhook URL to receive notifications when documents are processed.</p>
                              <div className="mt-2 text-xs">
                                <p><span className="font-semibold">Body:</span> <code className="bg-muted px-1">{`{"webhook_url": "https://your-server.com/webhook", "events": ["document.completed", "document.failed"]}`}</code></p>
                              </div>
                            </div>

                            <div className="bg-slate-50 dark:bg-slate-950/30 p-3 rounded-none border-l-4 border-slate-500">
                              <p className="font-mono text-sm font-bold">GET /api/external/v1/webhooks</p>
                              <p className="text-xs text-muted-foreground mt-1">List configured webhooks for your organization.</p>
                            </div>
                          </div>
                        </div>

                        {/* Response Format */}
                        <div className="border border-border rounded-none p-4 bg-slate-50 dark:bg-slate-950/30">
                          <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                            <Info className="h-5 w-5" />
                            BillPortal JSON Format Structure
                          </h3>
                          <p className="text-xs text-muted-foreground mb-3">When BillPortal Format is enabled, parsed data follows this structure:</p>
                          <div className="bg-slate-900 text-slate-100 p-3 rounded text-[10px] font-mono overflow-x-auto max-h-64">
                            <pre>{`{
  "billTrackId": "uuid",
  "customerName": "ACME CORP",
  "supplierName": "UTILITY CO",
  "invoiceNumber": "INV-12345",
  "invoiceDate": "2025-01-15",
  "dueDate": "2025-02-01",
  "accountNumber": "ACC-001",
  "totalAmountDue": 1234.56,
  "currentBillAmount": 1100.00,
  "previousBillAmount": 134.56,
  "isConsolidateBill": "YES" | "NO",
  "parseridentifiersdata": [
    {
      "identifier": "meter-account-no",
      "utilityType": "Electricity",
      "siteAddress": "123 Main St",
      "periodFrom": "2025-01-01",
      "periodTo": "2025-01-31",
      "serviceTotal": 500.00,
      "serviceUsage": 1500,
      "parserchargesdata": [
        {
          "meterNumber": "MTR-001",
          "category": "Energy",
          "lineItem": "Usage Charge",
          "quantity": "1500",
          "quantityUOM": "kWh",
          "rate": 0.10,
          "total": 150.00
        }
      ]
    }
  ]
}`}</pre>
                          </div>
                        </div>

                        {/* Tips */}
                        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-none p-4">
                          <h3 className="font-bold text-sm mb-2 flex items-center gap-2 text-amber-800 dark:text-amber-300">
                            <AlertTriangle className="h-4 w-4" />
                            Important Notes
                          </h3>
                          <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-1 list-disc list-inside">
                            <li>Enable "BillPortal Format Export" above for automatic format conversion</li>
                            <li>Use <code className="bg-amber-100 dark:bg-amber-900 px-1">external_id</code> to track documents with your internal IDs</li>
                            <li>Poll status endpoint or configure webhooks to know when processing completes</li>
                            <li>Rate limit: 200 uploads per hour per organization</li>
                          </ul>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="space-y-6">
                  {/* BillPortal Format Integration */}
                  <div className="border border-border rounded-none p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-bold text-lg">BillPortal Format Export</h3>
                          <Badge variant="outline" className="rounded-none text-xs">Beta</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-4">
                          Automatically convert parsed bills to BillPortal export format for integration with external systems.
                          When enabled, all newly processed bills will be transformed and stored in the standardized format.
                        </p>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="enable_billportal_format"
                            data-testid="enable-billportal-format-checkbox"
                            checked={integrationSettings.enable_billportal_format}
                            onChange={(e) => setIntegrationSettings({
                              ...integrationSettings,
                              enable_billportal_format: e.target.checked
                            })}
                            className="h-4 w-4 rounded-none border-border"
                          />
                          <Label htmlFor="enable_billportal_format" className="text-sm cursor-pointer">
                            Enable BillPortal Format
                          </Label>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    className="rounded-none font-bold uppercase"
                    onClick={handleSaveIntegrationSettings}
                    disabled={saving}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Integration Settings'}
                  </Button>
                  
                  {/* API Key Management */}
                  <div className="border border-border rounded-none p-6 mt-6">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-bold text-lg">External API Access</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Generate an API key to allow external systems to pull BillPortal format data via REST API.
                    </p>
                    
                    {/* API Key Status */}
                    <div className="bg-muted/50 p-4 rounded-none mb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">API Key Status</p>
                          <div className="flex items-center gap-2 mt-1">
                            {apiKeyStatus.hasKey ? (
                              apiKeyStatus.isActive ? (
                                <Badge variant="outline" className="rounded-none text-xs bg-green-50 text-green-700 border-green-200">Active</Badge>
                              ) : (
                                <Badge variant="outline" className="rounded-none text-xs bg-red-50 text-red-700 border-red-200">Revoked</Badge>
                              )
                            ) : (
                              <Badge variant="outline" className="rounded-none text-xs">Not Generated</Badge>
                            )}
                            {apiKeyStatus.createdAt && (
                              <span className="text-xs text-muted-foreground">
                                Created: {new Date(apiKeyStatus.createdAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-none"
                            onClick={handleGenerateApiKey}
                            disabled={generatingKey}
                          >
                            {generatingKey ? 'Generating...' : apiKeyStatus.hasKey ? 'Regenerate Key' : 'Generate Key'}
                          </Button>
                          {apiKeyStatus.hasKey && apiKeyStatus.isActive && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-none text-red-600 hover:text-red-700"
                              onClick={handleRevokeApiKey}
                            >
                              Revoke
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Show newly generated API key */}
                    {newApiKey && (
                      <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-none mb-4">
                        <p className="text-sm font-medium text-yellow-800 mb-2">
                          ⚠️ Copy your API key now! It won't be shown again.
                        </p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 bg-white p-2 text-sm font-mono border border-yellow-300 rounded-none overflow-x-auto">
                            {newApiKey}
                          </code>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-none shrink-0"
                            onClick={() => copyToClipboard(newApiKey)}
                          >
                            Copy
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* API Documentation */}
                    <div className="text-sm text-muted-foreground space-y-2">
                      <p className="font-medium">API Endpoints:</p>
                      
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-foreground mb-1">📤 Upload Bills:</p>
                        <ul className="list-disc list-inside space-y-1 text-xs font-mono">
                          <li>POST /api/external/v1/documents/upload (multipart/form-data)</li>
                          <li>GET /api/external/v1/documents/{'{documentId}'} (check status)</li>
                        </ul>
                      </div>
                      
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-foreground mb-1">📥 Export Data:</p>
                        <ul className="list-disc list-inside space-y-1 text-xs font-mono">
                          <li>GET /api/external/v1/exports?pulled=false</li>
                          <li>GET /api/external/v1/exports/{'{billTrackId}'}</li>
                          <li>POST /api/external/v1/exports/mark-pulled</li>
                        </ul>
                      </div>
                      
                      <p className="text-xs mt-2">
                        Use header: <code className="bg-muted px-1">X-API-Key: your_api_key</code>
                      </p>
                      
                      <div className="mt-4 p-3 bg-muted/50 rounded-none border border-border">
                        <p className="text-xs font-semibold text-foreground mb-2">Example: Upload PDF via cURL</p>
                        <code className="text-[10px] font-mono block whitespace-pre-wrap break-all">
{`curl -X POST "${window.location.origin}/api/external/v1/documents/upload" \\
  -H "X-API-Key: your_api_key" \\
  -F "file=@/path/to/bill.pdf"`}
                        </code>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && isAdmin && (
              <div className="border border-border rounded-none bg-card" data-testid="users-tab-panel">
                <div className="p-6 border-b border-border flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-4 flex-wrap">
                    <h2 className="text-2xl font-bold tracking-tight">Team Members</h2>
                    <Select
                      value={userStatusFilter}
                      onValueChange={setUserStatusFilter}
                    >
                      <SelectTrigger className="w-32 rounded-none h-8 text-xs" data-testid="users-status-filter">
                        <SelectValue placeholder="Filter" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="invited">Invited</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Search name or email..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="w-64 rounded-none h-8 text-xs"
                      data-testid="users-search-input"
                    />
                    <span className="text-sm text-muted-foreground" data-testid="users-count">
                      {users
                        .filter(u => userStatusFilter === 'all' || u.status === userStatusFilter)
                        .filter(u => {
                          if (!userSearch) return true;
                          const q = userSearch.toLowerCase();
                          return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                        })
                        .length} user(s)
                    </span>
                  </div>
                  <Button
                    className="rounded-none"
                    onClick={() => setShowInvite(!showInvite)}
                    data-testid="invite-user-btn"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Invite User
                  </Button>
                </div>

                {showInvite && (
                  <div className="p-6 border-b border-border bg-muted/30">
                    <form onSubmit={handleInviteUser} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs uppercase mb-1 block">Email</Label>
                          <Input
                            type="email"
                            className="rounded-none"
                            value={inviteForm.email}
                            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                            placeholder="user@example.com"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-xs uppercase mb-1 block">Name</Label>
                          <Input
                            className="rounded-none"
                            value={inviteForm.name}
                            onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                            placeholder="User name"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs uppercase mb-1 block">Role</Label>
                        <Select
                          value={inviteForm.role}
                          onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
                        >
                          <SelectTrigger className="rounded-none">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin - Full access</SelectItem>
                            <SelectItem value="editor">Editor - Can edit documents</SelectItem>
                            <SelectItem value="viewer">Viewer - Read only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" className="rounded-none">Send Invite</Button>
                        <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowInvite(false)}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="users-table">
                    <thead className="bg-muted/40 border-b border-border">
                      <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                        <th className="text-left px-4 py-2.5 font-semibold">User</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Role</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Last Login</th>
                        <th className="text-center px-4 py-2.5 font-semibold">
                          <div className="inline-flex items-center gap-1">
                            PF Review Authorisation
                            <span title="Email this user when a new Payment File is published for review" className="text-muted-foreground">
                              <Info className="h-3 w-3" />
                            </span>
                          </div>
                        </th>
                        <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users
                        .filter(member => userStatusFilter === 'all' || member.status === userStatusFilter)
                        .filter(member => {
                          if (!userSearch) return true;
                          const q = userSearch.toLowerCase();
                          return (member.name || '').toLowerCase().includes(q) || (member.email || '').toLowerCase().includes(q);
                        })
                        .map((member) => (
                        <tr key={member.id} className="border-b border-border/60 hover:bg-muted/20 transition-colors" data-testid={`user-row-${member.user_id}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium">{member.name || '—'}</div>
                            <div className="text-xs text-muted-foreground">{member.email}</div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              className={`rounded-none text-[10px] ${
                                member.status === 'inactive' ? 'bg-red-100 text-red-800' :
                                member.status === 'invited' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}
                            >
                              {member.status || 'active'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Select
                              value={member.role}
                              onValueChange={(value) => handleUpdateRole(member.user_id, value)}
                              disabled={member.user_id === user?.id}
                            >
                              <SelectTrigger className="w-28 rounded-none h-7 text-xs" data-testid={`user-role-${member.user_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="editor">Editor</SelectItem>
                                <SelectItem value="viewer">Viewer</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {member.status === 'invited'
                              ? <>Invited {formatDateTime(member.invited_at)}</>
                              : formatDateTime(member.last_login_at)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Switch
                              checked={!!member.pf_reviewer}
                              onCheckedChange={(v) => handleTogglePfReviewer(member.user_id, v)}
                              disabled={member.status === 'inactive'}
                              data-testid={`pf-reviewer-switch-${member.user_id}`}
                            />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex items-center gap-1">
                              {member.user_id !== user?.id && (
                                <>
                                  {member.status === 'active' ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 rounded-none text-yellow-600"
                                      onClick={() => handleUpdateUserStatus(member.user_id, 'inactive')}
                                      title="Deactivate user"
                                      data-testid={`deactivate-user-${member.user_id}`}
                                    >
                                      <Power className="h-3.5 w-3.5" />
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 rounded-none text-green-600"
                                      onClick={() => handleUpdateUserStatus(member.user_id, 'active')}
                                      title="Activate user"
                                      data-testid={`activate-user-${member.user_id}`}
                                    >
                                      <Power className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 rounded-none text-destructive hover:text-destructive"
                                    onClick={() => handleRemoveUser(member.user_id)}
                                    title="Remove from organization"
                                    data-testid={`remove-user-${member.user_id}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {users.length === 0 && (
                        <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No team members yet</td></tr>
                      )}
                      {users.length > 0 && users.filter(u => {
                        if (userStatusFilter !== 'all' && u.status !== userStatusFilter) return false;
                        if (!userSearch) return true;
                        const q = userSearch.toLowerCase();
                        return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                      }).length === 0 && (
                        <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No users match your search</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}


            {/* Feature Visibility Tab */}
            {activeTab === 'feature-visibility' && isAdmin && (
              <FeatureVisibilityTab users={users} currentAccount={currentAccount} fetchUsers={fetchUsers} />
            )}

            {/* Inbox Tab */}
            {activeTab === 'inbox' && (
              <div className="border border-border rounded-none p-8 bg-card">
                <h2 className="text-2xl font-bold tracking-tight mb-6">Email Inbox</h2>
                <p className="text-muted-foreground mb-6">
                  Receive bills via email. Forward bills to your inbox address and they&apos;ll be automatically processed.
                </p>
                
                {inboxSettings ? (
                  <div className="space-y-6">
                    {/* Inbox Email */}
                    <div className="p-4 bg-muted/30 rounded-none">
                      <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">
                        Inbox Email Address
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          value={inboxSettings.inbox_email}
                          readOnly
                          className="rounded-none font-mono bg-background"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-none shrink-0"
                          onClick={() => copyToClipboard(inboxSettings.inbox_email)}
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Forward bills to this email address. PDF attachments and ZIP files will be automatically processed.
                      </p>
                    </div>

                    {/* Inbound is fully automated via AWS SES — no admin setup.
                        Show a brief status note to super admins only. */}
                    {isSuperAdmin() && (
                    <div className="p-3 bg-muted/30 border border-border rounded-none flex items-start gap-2" data-testid="inbound-ses-status">
                      <Mail className="h-4 w-4 mt-0.5 text-green-600 shrink-0" />
                      <p className="text-xs text-muted-foreground">
                        <strong>Inbound is fully automated via AWS SES</strong> — no setup required.
                        Emails to <span className="font-mono">*@inbound.billportal.io</span> are received by SES,
                        parsed, and routed straight into the Intake Hub.
                      </p>
                    </div>
                    )}

                    {/* Inbound migrated Mailgun → AWS SES (Jun 2026). The
                        POST /api/inbound-email/webhook endpoint is now called
                        internally by the billportal-ses-inbound Lambda; it is no
                        longer pasted into any third-party route. Downstream
                        pipeline (inbox_queue → parser → Intake Hub) is unchanged. */}

                    {/* Queue Stats */}
                    <div className="p-4 bg-muted/30 rounded-none">
                      <Label className="text-xs uppercase tracking-wide font-medium mb-3 block">
                        Inbox Queue Status
                      </Label>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-background rounded-none border border-border">
                          <div className="text-2xl font-bold">{inboxSettings.queue_stats?.pending || 0}</div>
                          <div className="text-xs text-muted-foreground">Pending</div>
                        </div>
                        <div className="text-center p-3 bg-background rounded-none border border-border">
                          <div className="text-2xl font-bold text-yellow-600">{inboxSettings.queue_stats?.processing || 0}</div>
                          <div className="text-xs text-muted-foreground">Processing</div>
                        </div>
                        <div className="text-center p-3 bg-background rounded-none border border-border">
                          <div className="text-2xl font-bold text-green-600">{inboxSettings.queue_stats?.completed || 0}</div>
                          <div className="text-xs text-muted-foreground">Completed</div>
                        </div>
                        <div className="text-center p-3 bg-background rounded-none border border-border">
                          <div className="text-2xl font-bold text-red-600">{inboxSettings.queue_stats?.failed || 0}</div>
                          <div className="text-xs text-muted-foreground">Failed</div>
                        </div>
                      </div>
                    </div>

                    {/* Instructions */}
                    <div className="p-4 border border-border rounded-none bg-blue-50 dark:bg-blue-950/20">
                      <h3 className="font-medium mb-2 flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        How to Use Your Inbox
                      </h3>
                      <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                        <li><strong>Forward bills</strong> to your inbox email address above</li>
                        <li>Attach <strong>PDFs directly</strong> or as a <strong>ZIP file</strong> containing multiple bills</li>
                        <li>Bills will be <strong>automatically extracted</strong> and appear in your Intake Hub</li>
                        <li>Look for the <Mail className="h-3 w-3 inline text-blue-500" /> icon to identify emailed bills</li>
                      </ol>
                      <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
                        <strong>Note:</strong> Email inbox is configured by the BillPortal platform. Simply forward bills to your unique inbox address - no setup required!
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Clock className="h-8 w-8 mx-auto text-muted-foreground animate-spin" />
                    <p className="text-muted-foreground mt-2">Loading inbox settings...</p>
                  </div>
                )}
              </div>
            )}

            {/* Trusted Devices Tab */}
            {activeTab === 'trusted-devices' && (
              <TrustedDevicesTab />
            )}

            {/* SFTP Configuration Tab */}
            {activeTab === 'sftp' && isAdmin && (
              <SftpConfigTab />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const FEATURE_FLAGS = [
  { key: 'pf_validation_warnings', label: 'PF Validation Warnings', description: 'Show overdue and due-soon warning icons on Payment File bills' },
  { key: 'pf_issue_flags', label: 'PF Issue Flags', description: 'Show issue badges (NO GL, NO ADDR, MISMATCH, etc.) on Payment File detail rows' },
  { key: 'bill_payment_recording', label: 'Bill Payment Recording', description: 'Allow recording payments directly from the Bill View page' },
  { key: 'bill_sync_trigger', label: 'Bill Sync Trigger', description: 'Allow triggering bill sync from the Bills page' },
];

function FeatureVisibilityTab({ users, currentAccount, fetchUsers }) {
  const [saving, setSaving] = useState({});

  const handleToggle = async (user, featureKey, checked) => {
    const userId = user.user_id || user.id;
    setSaving(prev => ({ ...prev, [`${userId}-${featureKey}`]: true }));
    try {
      const current = user.feature_visibility || {};
      const updated = { ...current, [featureKey]: checked };
      await api.put(`/accounts/${currentAccount.id}/users/${userId}/feature-visibility`, {
        feature_visibility: updated,
      });
      toast.success(`Updated ${featureKey.replace(/_/g, ' ')} for ${user.name}`);
      fetchUsers();
    } catch {
      toast.error('Failed to update feature visibility');
    } finally {
      setSaving(prev => ({ ...prev, [`${userId}-${featureKey}`]: false }));
    }
  };

  return (
    <div className="border border-border rounded-none bg-card" data-testid="feature-visibility-tab">
      <div className="p-6 border-b border-border">
        <h2 className="text-2xl font-bold tracking-tight">Feature Visibility</h2>
        <p className="text-sm text-muted-foreground mt-1">Control which features are visible to each user. Toggle features on/off per user.</p>
      </div>
      <div className="p-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground w-[200px]">User</th>
              {FEATURE_FLAGS.map(f => (
                <th key={f.key} className="text-center py-3 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground" title={f.description}>
                  {f.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.filter(u => u.status !== 'inactive').map(user => {
              const userId = user.user_id || user.id;
              const fv = user.feature_visibility || {};
              return (
                <tr key={userId} className="border-b border-border/50 hover:bg-muted/30" data-testid={`fv-row-${userId}`}>
                  <td className="py-3 px-2">
                    <div>
                      <p className="font-medium text-sm">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </td>
                  {FEATURE_FLAGS.map(f => (
                    <td key={f.key} className="text-center py-3 px-3">
                      <Switch
                        checked={fv[f.key] === true}
                        onCheckedChange={(checked) => handleToggle(user, f.key, checked)}
                        disabled={saving[`${userId}-${f.key}`]}
                        data-testid={`fv-${userId}-${f.key}`}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
            {users.length === 0 && (
              <tr><td colSpan={FEATURE_FLAGS.length + 1} className="text-center py-8 text-muted-foreground">No users found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


const TrustedDevicesTab = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/auth/trusted-devices');
      setDevices(data.devices || []);
    } catch {
      toast.error('Could not load trusted devices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const revoke = async (id, label) => {
    if (!window.confirm(`Revoke trusted access for "${label || id}"? You'll have to enter the verification code next time you sign in from that browser.`)) return;
    setRevoking(id);
    try {
      await api.delete(`/auth/trusted-devices/${id}`);
      toast.success('Trusted device revoked');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not revoke');
    } finally { setRevoking(null); }
  };

  const clearThisDevice = async () => {
    try {
      await api.post('/auth/trusted-devices/clear-cookie');
      toast.success('Trust cookie cleared on this device');
    } catch {
      toast.error('Could not clear');
    }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
  const fmtExpiry = (iso) => {
    if (!iso) return '—';
    try {
      const ms = new Date(iso).getTime() - Date.now();
      const d = Math.round(ms / (1000 * 60 * 60 * 24));
      if (d <= 0) return 'expired';
      if (d === 1) return 'tomorrow';
      return `in ${d} days`;
    } catch { return iso; }
  };

  const guessIcon = (label) => {
    const t = (label || '').toLowerCase();
    if (t.includes('iphone') || t.includes('android')) return Smartphone;
    return Monitor;
  };

  return (
    <div className="border border-border rounded-none p-8 bg-card" data-testid="trusted-devices-tab">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight mb-2 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" /> Trusted Devices
          </h2>
          <p className="text-muted-foreground text-sm max-w-2xl">
            Browsers you've marked "Trust this device for 30 days" on the
            verification step. They skip the OTP prompt at sign-in.
            Revoke any device you no longer recognise — the next sign-in
            from that browser will require the full verification code.
          </p>
        </div>
        <Button
          data-testid="td-clear-this-device"
          variant="outline"
          size="sm"
          className="rounded-none shrink-0"
          onClick={clearThisDevice}
        >
          Sign me out of trust on this browser
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">
          <Clock className="h-6 w-6 mx-auto animate-spin mb-2" /> Loading…
        </div>
      ) : devices.length === 0 ? (
        <div className="border border-dashed border-border rounded-none py-12 text-center text-muted-foreground">
          <ShieldCheck className="h-8 w-8 mx-auto opacity-30 mb-2" />
          <div className="font-medium">No trusted devices</div>
          <div className="text-xs mt-1">Check "Trust this device for 30 days" on the next sign-in to add this browser.</div>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="td-list">
          {devices.map((d) => {
            const Icon = guessIcon(d.device_label);
            return (
              <li
                key={d.id}
                data-testid={`td-row-${d.id}`}
                className="flex items-center justify-between gap-3 border border-border rounded-none p-4 hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="rounded-none bg-emerald-50 p-2 text-emerald-700 shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">
                      {d.device_label || 'Unknown device'}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Added {fmt(d.created_at)} · last used {fmt(d.last_used_at)}
                      {d.ip ? ` · IP ${d.ip}` : ''}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Expires {fmtExpiry(d.expires_at)}
                    </div>
                  </div>
                </div>
                <Button
                  data-testid={`td-revoke-${d.id}`}
                  variant="outline"
                  size="sm"
                  className="rounded-none shrink-0 text-rose-600 hover:bg-rose-50"
                  disabled={revoking === d.id}
                  onClick={() => revoke(d.id, d.device_label)}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  {revoking === d.id ? 'Revoking…' : 'Revoke'}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};


export default Settings;

// ───────────────────────────────────────────────────────────────────────────
// SFTP Configuration Tab
// Phase 1 — single SFTP endpoint per org. Credentials encrypted at rest
// (services.crypto AES-256-GCM). Secrets never re-displayed after save;
// the user must verify their identity via email OTP to reveal plaintext.
// ───────────────────────────────────────────────────────────────────────────
const SftpConfigTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState(null);
  const [testResult, setTestResult] = useState(null);
  // Fingerprint of the form state at the moment the LAST successful Test
  // ran — used by handleSave to require Test-passes-against-current-input.
  const [lastTestedFingerprint, setLastTestedFingerprint] = useState(null);

  // Org context — Super Admins may have multiple accounts in localStorage
  // but all reads/writes should scope to the currently selected one.
  const currentAccountStr = localStorage.getItem('current_account');
  const currentAccount = currentAccountStr ? JSON.parse(currentAccountStr) : null;
  const accountId = currentAccount?.id;
  const params = accountId ? { account_id: accountId } : {};

  // Editable form state. Secrets stay empty unless the user types — that
  // way Save preserves the encrypted blob (the backend treats `null` as
  // "keep existing").
  const [form, setForm] = useState({
    name: 'Bank SFTP',
    host: '',
    port: 22,
    username: '',
    auth_type: 'password',
    password: '',
    private_key: '',
    passphrase: '',
    remote_folder: '/',
    host_fingerprint: '',
  });

  // Credentials lock: when a config with secrets already exists, the form
  // is hidden behind an OTP gate. The user must click "Verify identity"
  // and successfully enter the emailed code before viewing or editing.
  // After Save, we re-lock automatically.
  const [credsUnlocked, setCredsUnlocked] = useState(false);

  // Reveal flow
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealStep, setRevealStep] = useState('idle'); // 'idle' | 'sent'
  const [revealRequestId, setRevealRequestId] = useState(null);
  const [revealCode, setRevealCode] = useState('');
  const [revealSending, setRevealSending] = useState(false);
  const [revealedSecrets, setRevealedSecrets] = useState(null);
  const [showPasswordPlain, setShowPasswordPlain] = useState(false);
  const [showKeyPlain, setShowKeyPlain] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/sftp-config', { params });
      setConfig(data.config || null);
      if (data.config) {
        setForm((f) => ({
          ...f,
          name: data.config.name || 'Bank SFTP',
          host: data.config.host || '',
          port: data.config.port || 22,
          username: data.config.username || '',
          auth_type: data.config.auth_type || 'password',
          remote_folder: data.config.remote_folder || '/',
          host_fingerprint: data.config.host_fingerprint || '',
          // secrets always blank — backend never sends them
          password: '',
          private_key: '',
          passphrase: '',
        }));
      }
    } catch (e) {
      // 404-ish — no config yet is a normal state for new orgs.
      if (e?.response?.status && e.response.status !== 404) {
        toast.error('Could not load SFTP configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!form.host || !form.username) {
      toast.error('Host and Username are required');
      return;
    }
    // If the user typed a NEW secret (password/key/passphrase) we require a
    // passing connection test against THAT secret before allowing save —
    // mirrors the vendor-ACH verify-before-save flow and prevents
    // unreachable credentials from being written.
    const hasNewSecret = !!(form.password || form.private_key || form.passphrase);
    if (hasNewSecret) {
      const passedAgainstCurrentForm = (
        testResult?.success
        && lastTestedFingerprint
        === `${form.host}|${form.username}|${form.auth_type}|${form.password ? '1' : '0'}|${form.private_key ? '1' : '0'}|${form.passphrase ? '1' : '0'}`
      );
      if (!passedAgainstCurrentForm) {
        toast.error('Click "Test connection" first — credentials must verify against the bank before saving.');
        return;
      }
    }
    setSaving(true);
    try {
      // Build payload — only send secret fields if the user actually typed
      // something, so unchanged blobs are preserved server-side.
      const payload = {
        name: form.name?.trim() || 'Bank SFTP',
        host: form.host.trim(),
        port: Number(form.port) || 22,
        username: form.username.trim(),
        auth_type: form.auth_type,
        remote_folder: form.remote_folder?.trim() || '/',
        host_fingerprint: form.host_fingerprint?.trim() || null,
      };
      if (form.password) payload.password = form.password;
      if (form.private_key) payload.private_key = form.private_key;
      if (form.passphrase) payload.passphrase = form.passphrase;

      const { data } = await api.put('/sftp-config', payload);
      setConfig(data.config);
      // Wipe local secret state so it's never visible again.
      setForm((f) => ({ ...f, password: '', private_key: '', passphrase: '' }));
      // Re-lock the form — modifying the saved config requires another
      // OTP verification (matches the bank-grade SFTP credential rule).
      setCredsUnlocked(false);
      setLastTestedFingerprint(null);
      setTestResult(null);
      toast.success('SFTP configuration saved · credentials locked. Re-verify to modify.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save SFTP configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      // Pass any in-flight edits as overrides so users can "test before save".
      const overrides = {
        host: form.host?.trim() || undefined,
        port: Number(form.port) || undefined,
        username: form.username?.trim() || undefined,
        auth_type: form.auth_type || undefined,
        remote_folder: form.remote_folder?.trim() || undefined,
      };
      if (form.password) overrides.password = form.password;
      if (form.private_key) overrides.private_key = form.private_key;
      if (form.passphrase) overrides.passphrase = form.passphrase;

      const { data } = await api.post('/sftp-config/test', overrides, { params });
      setTestResult(data);
      if (data.success) {
        // Record what we just successfully tested so handleSave can verify
        // the form state hasn't drifted since.
        setLastTestedFingerprint(
          `${form.host}|${form.username}|${form.auth_type}|${form.password ? '1' : '0'}|${form.private_key ? '1' : '0'}|${form.passphrase ? '1' : '0'}`
        );
        toast.success('SFTP connection verified');
      } else {
        setLastTestedFingerprint(null);
        toast.error(data.message || 'Connection test failed');
      }
      // refresh saved test status badge
      await load();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Connection test failed';
      setTestResult({ success: false, message: msg, steps: [] });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const startReveal = async () => {
    setRevealSending(true);
    try {
      const { data } = await api.post('/sftp-config/request-reveal', {}, { params });
      setRevealRequestId(data.request_id);
      setRevealStep('sent');
      if (data.email_sent) {
        // Real email went out — do NOT surface the dev-only `test_otp`
        // payload to the user; E2E scripts can still read it from the JSON.
        toast.success(`Verification code emailed to ${data.email}`);
      } else if (data.test_otp) {
        // Email pipeline broken but we have a fallback code so the admin
        // isn't locked out. This is the only path that may show the code.
        toast.error(
          `Email delivery failed (${data.email_error || 'check provider config'}). ` +
          `Fallback code: ${data.test_otp}`,
          { duration: 12000 }
        );
      } else {
        toast.error('Could not send verification code — please contact support.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send verification code');
    } finally {
      setRevealSending(false);
    }
  };

  const submitReveal = async () => {
    if (!revealCode || !revealRequestId) return;
    setRevealSending(true);
    try {
      const { data } = await api.post('/sftp-config/reveal', {
        request_id: revealRequestId,
        code: revealCode,
      }, { params });
      const cfg = data.config || {};
      setRevealedSecrets(cfg);
      // Auto-populate the editable form so the user can immediately
      // view AND modify the unlocked values. Secrets are kept in-memory
      // only for this session — never persisted to localStorage.
      setForm((prev) => ({
        ...prev,
        name: cfg.name || prev.name,
        host: cfg.host || prev.host,
        port: cfg.port ?? prev.port,
        username: cfg.username || prev.username,
        auth_type: cfg.auth_type || prev.auth_type,
        remote_folder: cfg.remote_folder || prev.remote_folder,
        host_fingerprint: cfg.host_fingerprint || prev.host_fingerprint,
        password: cfg.password || '',
        private_key: cfg.private_key || '',
        passphrase: cfg.passphrase || '',
      }));
      setCredsUnlocked(true);
      setRevealCode('');
      // Close the modal — the unlocked form is the modify surface now.
      setRevealOpen(false);
      setRevealStep('idle');
      setRevealRequestId(null);
      toast.success('Verified — credentials unlocked for this session');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verification failed');
    } finally {
      setRevealSending(false);
    }
  };

  const closeReveal = () => {
    setRevealOpen(false);
    setRevealStep('idle');
    setRevealRequestId(null);
    setRevealCode('');
    setRevealedSecrets(null);
    setShowPasswordPlain(false);
    setShowKeyPlain(false);
  };

  const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleString() : '—'; } catch { return iso; } };

  return (
    <div className="border border-border rounded-none p-8 bg-card" data-testid="sftp-config-tab">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight mb-2 flex items-center gap-2">
            <Server className="h-5 w-5 text-emerald-600" /> SFTP Configuration
          </h2>
          <p className="text-muted-foreground text-sm max-w-2xl">
            Configure your bank&apos;s SFTP endpoint for transmitting ACH files
            (CCD+ and CTX NACHA). Credentials are stored encrypted with
            AES-256-GCM and never displayed after saving — to view or change
            them, you must re-verify your account.
          </p>
        </div>
        {config && (
          <Button
            data-testid="sftp-reveal-btn"
            variant="outline"
            size="sm"
            className="rounded-none shrink-0"
            onClick={() => { setRevealOpen(true); setRevealStep('idle'); }}
          >
            <KeyRound className="h-4 w-4 mr-1" />
            {credsUnlocked ? 'Re-verify identity' : 'Verify identity to modify'}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">
          <Clock className="h-6 w-6 mx-auto animate-spin mb-2" /> Loading…
        </div>
      ) : (config && (config.password_set || config.private_key_set) && !credsUnlocked) ? (
        /* ─── LOCK SCREEN ────────────────────────────────────────────────
           Credentials exist. They must remain hidden + immutable until
           the user proves they own the registered email by entering an
           OTP. Once verified, the form below replaces this screen and
           is auto-populated with the revealed plaintext. */
        <div className="space-y-6" data-testid="sftp-lock-screen">
          {/* Read-only summary so admins can confirm what's saved without unlocking */}
          <div className="border border-border rounded-none p-4 bg-muted/30 flex flex-wrap items-center gap-3 text-sm">
            <Badge variant="outline" className="rounded-none">Credentials saved · locked</Badge>
            {config.last_test_status === 'success' && (
              <Badge className="rounded-none bg-emerald-100 text-emerald-800 border-emerald-200">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Last test passed · {fmt(config.last_test_at)}
              </Badge>
            )}
            {config.last_test_status === 'failure' && (
              <Badge className="rounded-none bg-rose-100 text-rose-800 border-rose-200">
                <AlertTriangle className="h-3 w-3 mr-1" /> Last test failed · {fmt(config.last_test_at)}
              </Badge>
            )}
            <span className="text-xs text-muted-foreground ml-auto">Updated {fmt(config.updated_at)}</span>
          </div>

          {/* Locked panel */}
          <div className="border border-amber-200 bg-amber-50/50 rounded-none p-8 flex flex-col items-center text-center gap-4">
            <div className="h-14 w-14 rounded-none bg-amber-100 border border-amber-300 flex items-center justify-center">
              <KeyRound className="h-7 w-7 text-amber-700" />
            </div>
            <div className="space-y-1 max-w-lg">
              <h3 className="text-lg font-semibold tracking-tight">Credentials are locked</h3>
              <p className="text-sm text-muted-foreground">
                Saved SFTP credentials are encrypted at rest and never displayed in the
                clear. To view or modify them you must re-verify your account — a
                6-digit code will be sent to your registered email.
              </p>
            </div>

            {/* Show what's saved as last-4 / fingerprint hints (safe) */}
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs text-muted-foreground mt-2">
              <div><span className="font-medium text-slate-700">Host:</span> {config.host}</div>
              <div><span className="font-medium text-slate-700">Username:</span> {config.username}</div>
              <div><span className="font-medium text-slate-700">Port:</span> {config.port}</div>
              <div><span className="font-medium text-slate-700">Folder:</span> {config.remote_folder}</div>
              {config.password_set && (
                <div>
                  <span className="font-medium text-slate-700">Password:</span>{' '}
                  •••• ending in {config.password_last4 || '••••'}
                </div>
              )}
              {config.private_key_set && (
                <div className="col-span-2">
                  <span className="font-medium text-slate-700">Key:</span>{' '}
                  {config.private_key_fingerprint || 'fingerprint hidden'}
                </div>
              )}
            </div>

            <Button
              data-testid="sftp-unlock-btn"
              className="rounded-none mt-2"
              onClick={() => { setRevealOpen(true); setRevealStep('idle'); }}
            >
              <KeyRound className="h-4 w-4 mr-2" /> Verify identity to view or modify
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Current state strip */}
          {config && (
            <div className="border border-border rounded-none p-4 bg-muted/30 flex flex-wrap items-center gap-3 text-sm" data-testid="sftp-state-strip">
              <Badge variant="outline" className="rounded-none">
                {config.password_set || config.private_key_set ? 'Credentials saved' : 'No credentials'}
              </Badge>
              {config.last_test_status === 'success' && (
                <Badge className="rounded-none bg-emerald-100 text-emerald-800 border-emerald-200">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Last test passed · {fmt(config.last_test_at)}
                </Badge>
              )}
              {config.last_test_status === 'failure' && (
                <Badge className="rounded-none bg-rose-100 text-rose-800 border-rose-200">
                  <AlertTriangle className="h-3 w-3 mr-1" /> Last test failed · {fmt(config.last_test_at)}
                </Badge>
              )}
              {!config.last_test_status && (
                <span className="text-xs text-muted-foreground">Never tested</span>
              )}
              <span className="text-xs text-muted-foreground ml-auto">Updated {fmt(config.updated_at)}</span>
            </div>
          )}

          {/* Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">Connection Name</Label>
              <Input
                data-testid="sftp-name-input"
                className="rounded-none"
                placeholder="Bank SFTP"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">Authentication</Label>
              <Select
                value={form.auth_type}
                onValueChange={(v) => setForm({ ...form, auth_type: v })}
              >
                <SelectTrigger data-testid="sftp-auth-type" className="rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="password">Password</SelectItem>
                  <SelectItem value="private_key">Private Key</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">Host</Label>
                <Input
                  data-testid="sftp-host-input"
                  className="rounded-none font-mono"
                  placeholder="sftp.bank.example.com"
                  value={form.host}
                  onChange={(e) => setForm({ ...form, host: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">Port</Label>
                <Input
                  data-testid="sftp-port-input"
                  type="number"
                  className="rounded-none font-mono"
                  placeholder="22"
                  value={form.port}
                  onChange={(e) => setForm({ ...form, port: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">Username</Label>
              <Input
                data-testid="sftp-username-input"
                className="rounded-none font-mono"
                placeholder="bp_ach_user"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">Remote Folder</Label>
              <Input
                data-testid="sftp-folder-input"
                className="rounded-none font-mono"
                placeholder="/incoming/ach"
                value={form.remote_folder}
                onChange={(e) => setForm({ ...form, remote_folder: e.target.value })}
              />
            </div>

            {form.auth_type === 'password' ? (
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wide font-medium mb-2 block flex items-center justify-between">
                  <span>Password</span>
                  {config?.password_set && !form.password && (
                    <span className="text-[10px] normal-case font-normal text-muted-foreground">
                      Saved · ends in {config.password_last4 || '••••'} (leave blank to keep)
                    </span>
                  )}
                </Label>
                <Input
                  data-testid="sftp-password-input"
                  type="password"
                  className="rounded-none font-mono"
                  placeholder={config?.password_set ? '••••••••' : 'Enter password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  autoComplete="new-password"
                />
              </div>
            ) : (
              <>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wide font-medium mb-2 block flex items-center justify-between">
                    <span>Private Key (PEM/OpenSSH)</span>
                    {config?.private_key_set && !form.private_key && (
                      <span className="text-[10px] normal-case font-normal text-muted-foreground">
                        Saved · {config.private_key_fingerprint || 'fingerprint hidden'} (leave blank to keep)
                      </span>
                    )}
                  </Label>
                  <textarea
                    data-testid="sftp-privatekey-input"
                    className="w-full min-h-[120px] rounded-none font-mono text-xs p-3 border border-input bg-background"
                    placeholder={config?.private_key_set ? '••••• Private key saved — paste a new one to replace •••••' : '-----BEGIN OPENSSH PRIVATE KEY-----\n...'}
                    value={form.private_key}
                    onChange={(e) => setForm({ ...form, private_key: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">
                    Key Passphrase (optional)
                  </Label>
                  <Input
                    data-testid="sftp-passphrase-input"
                    type="password"
                    className="rounded-none font-mono"
                    placeholder={config?.passphrase_set ? '••••••••' : 'Leave empty if key is unencrypted'}
                    value={form.passphrase}
                    onChange={(e) => setForm({ ...form, passphrase: e.target.value })}
                    autoComplete="new-password"
                  />
                </div>
              </>
            )}

            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">
                Host Key Fingerprint (optional)
              </Label>
              <Input
                data-testid="sftp-fingerprint-input"
                className="rounded-none font-mono text-xs"
                placeholder="SHA256:xxxxxxxxxxxxxxxxxxxx"
                value={form.host_fingerprint}
                onChange={(e) => setForm({ ...form, host_fingerprint: e.target.value })}
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Reference only in Phase 1 — host key pinning will be enforced when ACH transmission goes live.
              </p>
            </div>
          </div>

          {/* Test Result */}
          {testResult && (
            <div
              data-testid="sftp-test-result"
              className={`border rounded-none p-4 ${testResult.success ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}
            >
              <div className="flex items-center gap-2 font-semibold text-sm">
                {testResult.success ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-rose-700" />
                )}
                {testResult.success ? 'Connection verified' : 'Connection failed'}
              </div>
              <p className="text-xs mt-1 text-foreground/80">{testResult.message}</p>
              {testResult.steps && testResult.steps.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs">
                  {testResult.steps.map((s, i) => (
                    <li key={i} className="flex items-start gap-2" data-testid={`sftp-test-step-${i}`}>
                      {s.ok ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                      ) : (
                        <AlertTriangle className="h-3.5 w-3.5 text-rose-600 mt-0.5 shrink-0" />
                      )}
                      <span><span className="font-medium">{s.label}:</span> {s.message || (s.ok ? 'OK' : 'not reached')}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button
              data-testid="sftp-save-btn"
              className="rounded-none font-bold uppercase"
              onClick={handleSave}
              disabled={saving}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving…' : 'Save Configuration'}
            </Button>
            <Button
              data-testid="sftp-test-btn"
              variant="outline"
              className="rounded-none font-bold uppercase"
              onClick={handleTest}
              disabled={testing}
            >
              <PlugZap className="h-4 w-4 mr-2" />
              {testing ? 'Testing…' : 'Test Connection'}
            </Button>
            <span className="text-[11px] text-muted-foreground">
              Test runs an end-to-end check: TCP reachability → authentication → remote folder listing.
            </span>
          </div>
        </div>
      )}

      {/* Reveal Modal */}
      <Dialog open={revealOpen} onOpenChange={(o) => (o ? setRevealOpen(true) : closeReveal())}>
        <DialogContent className="rounded-none max-w-lg" data-testid="sftp-reveal-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-emerald-600" />
              Verify identity to view or modify credentials
            </DialogTitle>
          </DialogHeader>

          {!revealedSecrets ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                For security, saved SFTP passwords and private keys are
                encrypted at rest and never sent to the browser unless your
                account is re-verified. Confirm a 6-digit code we&apos;ll
                email to your registered address to unlock the form.
              </p>
              {revealStep === 'idle' ? (
                <Button
                  data-testid="sftp-reveal-send-otp"
                  className="rounded-none w-full font-bold uppercase"
                  onClick={startReveal}
                  disabled={revealSending}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  {revealSending ? 'Sending…' : 'Email me a verification code'}
                </Button>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">
                      Verification Code
                    </Label>
                    <Input
                      data-testid="sftp-reveal-code-input"
                      className="rounded-none font-mono text-lg tracking-[0.4em]"
                      placeholder="000000"
                      maxLength={6}
                      value={revealCode}
                      onChange={(e) => setRevealCode(e.target.value.replace(/\D/g, ''))}
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Code expires in 5 minutes. 5 attempts max.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid="sftp-reveal-submit"
                      className="rounded-none font-bold uppercase flex-1"
                      onClick={submitReveal}
                      disabled={revealSending || revealCode.length < 4}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      {revealSending ? 'Verifying…' : 'Verify & reveal'}
                    </Button>
                    <Button
                      variant="outline"
                      className="rounded-none"
                      onClick={startReveal}
                      disabled={revealSending}
                      title="Send a new code"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3" data-testid="sftp-reveal-payload">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Secrets — shown once for this session
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium mb-1 block">Username</Label>
                <Input readOnly value={revealedSecrets.username || ''} className="rounded-none font-mono" />
              </div>
              {revealedSecrets.password != null && (
                <div>
                  <Label className="text-xs uppercase tracking-wide font-medium mb-1 block">Password</Label>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      type={showPasswordPlain ? 'text' : 'password'}
                      value={revealedSecrets.password || ''}
                      className="rounded-none font-mono"
                      data-testid="sftp-reveal-password"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-none shrink-0"
                      onClick={() => setShowPasswordPlain((v) => !v)}
                    >
                      {showPasswordPlain ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-none shrink-0"
                      onClick={() => { navigator.clipboard.writeText(revealedSecrets.password || ''); toast.success('Copied'); }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              {revealedSecrets.private_key != null && (
                <div>
                  <Label className="text-xs uppercase tracking-wide font-medium mb-1 block">Private Key</Label>
                  <div className="flex gap-2 items-start">
                    {showKeyPlain ? (
                      <textarea
                        readOnly
                        value={revealedSecrets.private_key || ''}
                        className="flex-1 min-h-[140px] rounded-none font-mono text-[10px] p-2 border border-input bg-background"
                        data-testid="sftp-reveal-privatekey"
                      />
                    ) : (
                      <Input
                        readOnly
                        type="password"
                        value="••••••••••••••••••••"
                        className="rounded-none font-mono"
                      />
                    )}
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        onClick={() => setShowKeyPlain((v) => !v)}
                      >
                        {showKeyPlain ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        onClick={() => { navigator.clipboard.writeText(revealedSecrets.private_key || ''); toast.success('Copied'); }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {revealedSecrets.passphrase != null && (
                <div>
                  <Label className="text-xs uppercase tracking-wide font-medium mb-1 block">Passphrase</Label>
                  <Input
                    readOnly
                    type="password"
                    value={revealedSecrets.passphrase || ''}
                    className="rounded-none font-mono"
                  />
                </div>
              )}
              <Button
                data-testid="sftp-reveal-close"
                className="rounded-none w-full font-bold uppercase mt-2"
                onClick={closeReveal}
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PS_WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const PS_TIMEZONES = [
  { value: 'UTC', label: 'UTC (UST)' },
  { value: 'Asia/Kolkata', label: 'IST — India Standard Time' },
  { value: 'America/New_York', label: 'US Eastern (New York)' },
  { value: 'America/Chicago', label: 'US Central (Chicago)' },
  { value: 'America/Denver', label: 'US Mountain (Denver)' },
  { value: 'America/Los_Angeles', label: 'US Pacific (Los Angeles)' },
  { value: 'America/Phoenix', label: 'US Arizona (Phoenix)' },
];
const PS_CADENCES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'semimonthly', label: 'Twice a month' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'semiweekly', label: 'Twice a week' },
];
const PS_TZ_ABBR = {
  'UTC': 'UST', 'Asia/Kolkata': 'IST', 'America/New_York': 'ET',
  'America/Chicago': 'CT', 'America/Denver': 'MT',
  'America/Los_Angeles': 'PT', 'America/Phoenix': 'MST',
};
const fmtNextRunTz = (iso, tz) => {
  if (!iso) return null;
  try {
    const s = new Intl.DateTimeFormat('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz || 'UTC',
    }).format(new Date(iso));
    return `${s} ${PS_TZ_ABBR[tz] || tz || ''}`.trim();
  } catch { return new Date(iso).toLocaleString(); }
};
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const PaymentsScheduleTab = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [eligible, setEligible] = useState(null);

  const acctId = (() => {
    try { return JSON.parse(localStorage.getItem('current_account') || '{}').id; }
    catch { return undefined; }
  })();
  const params = { account_id: acctId };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/payment-schedule', { params });
      setCfg(data);
    } catch {
      toast.error('Could not load payment schedule');
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async () => {
    try {
      const { data } = await api.get('/payment-schedule/preview', { params });
      setEligible(data);
    } catch { /* non-blocking */ }
  };

  useEffect(() => { load(); loadPreview(); }, []);

  const set = (key, val) => setCfg((c) => ({ ...c, [key]: val }));
  const setDom = (idx, val) => setCfg((c) => {
    const arr = [...(c.days_of_month || [1, 15])];
    arr[idx] = Number(val);
    return { ...c, days_of_month: arr };
  });
  const setDow = (idx, val) => setCfg((c) => {
    const arr = [...(c.days_of_week || ['monday', 'thursday'])];
    arr[idx] = val;
    return { ...c, days_of_week: arr };
  });

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        enabled: cfg.enabled,
        cadence: cfg.cadence,
        day_of_month: cfg.day_of_month,
        days_of_month: cfg.days_of_month,
        day_of_week: cfg.day_of_week,
        days_of_week: cfg.days_of_week,
        time_of_day: cfg.time_of_day,
        timezone: cfg.timezone,
        min_bills: cfg.min_bills,
        notify_before_generate: cfg.notify_before_generate,
        require_approval: cfg.require_approval,
      };
      const { data } = await api.put('/payment-schedule', body, { params });
      setCfg(data);
      toast.success('Payment schedule saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const generateNow = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post('/payment-schedule/generate-now', {}, { params });
      if (data.generated) {
        toast.success(`Payment file ${data.batch_name} generated with ${data.total_bills} bill(s)`);
      } else {
        toast.info(`No payment file generated — ${data.eligible_count || 0} eligible bill(s)`);
      }
      loadPreview();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate payment file');
    } finally {
      setGenerating(false);
    }
  };

  const [testingEmail, setTestingEmail] = useState(false);
  const sendTestEmail = async () => {
    setTestingEmail(true);
    try {
      const { data } = await api.post('/payment-schedule/test-notification', {}, { params });
      toast.success(`Test email sent to ${data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading || !cfg) {
    return (
      <div className="border border-border rounded-none p-8 bg-card" data-testid="payments-schedule-tab">
        <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const nextRun = cfg.next_run_at ? fmtNextRunTz(cfg.next_run_at, cfg.timezone) : null;

  return (
    <div className="border border-border rounded-none p-8 bg-card space-y-8" data-testid="payments-schedule-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-emerald-600" /> Payments Schedule
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Schedule when this organisation&apos;s payment file is generated. At the scheduled
            time, validated &amp; approved bills (PaaS &gt; Validations &gt; Validation Passed) are
            assembled into a payment file that appears under Payments &gt; Payment Files.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Enabled</span>
          <Switch
            checked={cfg.enabled}
            onCheckedChange={(v) => set('enabled', v)}
            data-testid="ps-enabled-switch"
          />
        </div>
      </div>

      {eligible && (
        <div className="border border-border rounded-none p-4 bg-muted/30 flex flex-wrap items-center gap-4 text-sm" data-testid="ps-eligible-strip">
          <span className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <strong data-testid="ps-eligible-count">{eligible.eligible_count}</strong> bill(s) eligible now
          </span>
          <span className="text-muted-foreground">
            Validation: {eligible.validation_counts?.passed || 0} passed · {eligible.validation_counts?.failed || 0} failed
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
        <div className="space-y-2">
          <Label>Frequency</Label>
          <Select value={cfg.cadence} onValueChange={(v) => set('cadence', v)}>
            <SelectTrigger data-testid="ps-cadence-select" className="rounded-none"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PS_CADENCES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Time of day</Label>
          <Input
            type="time"
            value={cfg.time_of_day}
            onChange={(e) => set('time_of_day', e.target.value)}
            className="rounded-none"
            data-testid="ps-time"
          />
        </div>

        {cfg.cadence === 'monthly' && (
          <div className="space-y-2">
            <Label>Day of month</Label>
            <Select value={String(cfg.day_of_month)} onValueChange={(v) => set('day_of_month', Number(v))}>
              <SelectTrigger data-testid="ps-day-of-month" className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {cfg.cadence === 'semimonthly' && (
          <>
            <div className="space-y-2">
              <Label>First day of month</Label>
              <Select value={String((cfg.days_of_month || [1, 15])[0])} onValueChange={(v) => setDom(0, v)}>
                <SelectTrigger data-testid="ps-day-of-month" className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Second day of month</Label>
              <Select value={String((cfg.days_of_month || [1, 15])[1])} onValueChange={(v) => setDom(1, v)}>
                <SelectTrigger data-testid="ps-day-of-month-2" className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {cfg.cadence === 'weekly' && (
          <div className="space-y-2">
            <Label>Day of week</Label>
            <Select value={cfg.day_of_week} onValueChange={(v) => set('day_of_week', v)}>
              <SelectTrigger data-testid="ps-day-of-week" className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PS_WEEKDAYS.map((d) => <SelectItem key={d} value={d}>{cap(d)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {cfg.cadence === 'semiweekly' && (
          <>
            <div className="space-y-2">
              <Label>First day of week</Label>
              <Select value={(cfg.days_of_week || ['monday', 'thursday'])[0]} onValueChange={(v) => setDow(0, v)}>
                <SelectTrigger data-testid="ps-day-of-week" className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PS_WEEKDAYS.map((d) => <SelectItem key={d} value={d}>{cap(d)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Second day of week</Label>
              <Select value={(cfg.days_of_week || ['monday', 'thursday'])[1]} onValueChange={(v) => setDow(1, v)}>
                <SelectTrigger data-testid="ps-day-of-week-2" className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PS_WEEKDAYS.map((d) => <SelectItem key={d} value={d}>{cap(d)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        <div className="space-y-2">
          <Label>Timezone</Label>
          <Select value={cfg.timezone} onValueChange={(v) => set('timezone', v)}>
            <SelectTrigger data-testid="ps-timezone" className="rounded-none"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PS_TIMEZONES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {cfg.enabled && nextRun ? (
            <p className="text-xs text-primary flex items-center gap-1.5 pt-0.5" data-testid="ps-next-run-tz">
              <Clock className="h-3.5 w-3.5" /> Next run: <strong>{nextRun}</strong>
            </p>
          ) : (
            <p className="text-xs text-muted-foreground pt-0.5">
              Times are interpreted in this timezone. Save to compute the next run.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label>Minimum bills to generate</Label>
          <Input
            type="number"
            min={1}
            value={cfg.min_bills}
            onChange={(e) => set('min_bills', Number(e.target.value))}
            className="rounded-none"
            data-testid="ps-min-bills"
          />
        </div>
      </div>

      <div className="space-y-4 max-w-2xl border-t border-border pt-6">
        <label className="flex items-start gap-3 cursor-pointer" htmlFor="ps-notify">
          <Switch
            id="ps-notify"
            checked={!!cfg.notify_before_generate}
            onCheckedChange={(v) => set('notify_before_generate', v)}
            data-testid="ps-notify-switch"
          />
          <span className="text-sm">
            <span className="font-medium">Email notification before generating</span>
            <span className="block text-muted-foreground">
              Email all org admins ~6 hours before the scheduled time, letting them know the
              payment file will be generated today.
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={(e) => { e.preventDefault(); sendTestEmail(); }}
              disabled={testingEmail}
              data-testid="ps-test-email-btn"
            >
              {testingEmail ? <RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-2" />}
              Send test email to me
            </Button>
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer" htmlFor="ps-approval">
          <Switch
            id="ps-approval"
            checked={!!cfg.require_approval}
            onCheckedChange={(v) => set('require_approval', v)}
            data-testid="ps-approval-switch"
          />
          <span className="text-sm">
            <span className="font-medium">Require approval before generating</span>
            <span className="block text-muted-foreground">
              The generated payment file lands under Payments &gt; Payment Files as
              <strong> In Review</strong>. An admin reviews it, uses <strong>Add bill</strong>
              &nbsp;inside the file to pull in newly-approved bills, then approves it.
            </span>
          </span>
        </label>
      </div>

      {cfg.enabled && nextRun && (
        <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="ps-next-run">
          <Clock className="h-4 w-4" /> Next run: <strong>{nextRun}</strong>
        </p>
      )}
      {cfg.last_run_at && (
        <p className="text-xs text-muted-foreground" data-testid="ps-last-run">
          Last run: {new Date(cfg.last_run_at).toLocaleString()} · {cfg.last_run_status}
        </p>
      )}

      <div className="flex items-center gap-3 pt-2 border-t border-border">
        <Button onClick={save} disabled={saving} data-testid="ps-save-btn">
          {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Schedule
        </Button>
        <Button variant="outline" onClick={generateNow} disabled={generating} data-testid="ps-generate-now-btn">
          {generating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-2" />}
          Generate now
        </Button>
      </div>
    </div>
  );
};


// ── GL Code Configuration: global commodity-level GL codes for payment files ──
const KNOWN_UTILITIES = [
  { value: 'electricity', label: 'Electricity' },
  { value: 'gas', label: 'Gas' },
  { value: 'water', label: 'Water' },
  { value: 'sewer', label: 'Sewer' },
  { value: 'stormwater', label: 'Stormwater' },
  { value: 'chilledwater', label: 'Chilled Water' },
  { value: 'steam', label: 'Steam' },
  { value: 'trash', label: 'Trash' },
];
const utilityLabel = (c) => {
  const k = KNOWN_UTILITIES.find(u => u.value === c);
  if (k) return k.label;
  return (c || '').replace(/\b\w/g, (m) => m.toUpperCase());
};

const GlCodeConfigTab = ({ currentAccount }) => {
  const [rows, setRows] = useState([]);        // [{commodity, gl_code, stats}]
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newCommodity, setNewCommodity] = useState('');   // known value OR '__add__'
  const [newCustom, setNewCustom] = useState('');
  const [newGl, setNewGl] = useState('');
  const [editing, setEditing] = useState(null);       // commodity being edited
  const [editGl, setEditGl] = useState('');
  const [editCommodity, setEditCommodity] = useState('');  // known value OR '__add__'
  const [editCustom, setEditCustom] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const acctId = currentAccount?.id;

  const load = async () => {
    if (!acctId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/ubm/accounts/${acctId}/gl-codes`);
      const map = data.gl_by_commodity || {};
      setStats(data.stats || {});
      setRows(Object.keys(map).sort().map(c => ({ commodity: c, gl_code: map[c] })));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load GL code configuration');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [acctId]);

  const setGl = async (commodity, gl_code) => {
    await api.put(`/ubm/accounts/${acctId}/gl-codes/${encodeURIComponent(commodity)}`, { gl_code });
  };

  const handleAdd = async () => {
    const commodity = (newCommodity === '__add__' ? newCustom : newCommodity).trim().toLowerCase();
    const gl = newGl.trim();
    if (!commodity) { toast.error('Select or enter a utility'); return; }
    if (!gl) { toast.error('Enter a GL code'); return; }
    if (rows.some(r => r.commodity === commodity)) { toast.error(`A GL code for "${utilityLabel(commodity)}" already exists — edit it instead`); return; }
    setSaving(true);
    try {
      await setGl(commodity, gl);
      toast.success(`GL code set for ${utilityLabel(commodity)}`);
      setAddOpen(false); setNewCommodity(''); setNewCustom(''); setNewGl('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save GL code');
    } finally { setSaving(false); }
  };

  const startEdit = (r) => { setEditing(r.commodity); setEditGl(r.gl_code); setEditCommodity(r.commodity); setEditCustom(''); };
  const cancelEdit = () => { setEditing(null); setEditGl(''); setEditCommodity(''); setEditCustom(''); };

  const saveEdit = async (orig) => {
    const gl = editGl.trim();
    const newComm = (editCommodity === '__add__' ? editCustom : editCommodity).trim().toLowerCase();
    if (!newComm) { toast.error('Utility cannot be empty'); return; }
    if (!gl) { toast.error('Enter a GL code'); return; }
    if (newComm !== orig && rows.some(r => r.commodity === newComm)) { toast.error(`A GL code for "${utilityLabel(newComm)}" already exists`); return; }
    setSaving(true);
    try {
      if (newComm !== orig) {
        await setGl(newComm, gl);       // create under the new utility
        await setGl(orig, '');          // clear the old utility mapping
      } else {
        await setGl(orig, gl);
      }
      toast.success('GL code updated');
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update GL code');
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await setGl(deleteTarget, '');
      toast.success(`GL code removed for ${utilityLabel(deleteTarget)}`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove GL code');
    } finally { setSaving(false); }
  };

  const availableUtilities = KNOWN_UTILITIES.filter(u => !rows.some(r => r.commodity === u.value));

  return (
    <div className="space-y-6" data-testid="gl-code-config-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2"><Hash className="h-6 w-6" /> GL Code Configuration</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Set a global GL code for each utility type. These GL codes are applied automatically to matching utilities on your Payment Files. Manual per-meter overrides always take precedence.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="gl-add-btn" className="shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> Add GL Code
        </Button>
      </div>

      <div className="border border-border rounded-none bg-card">
        <table className="w-full text-sm" data-testid="gl-config-table">
          <thead>
            <tr className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Utility</th>
              <th className="px-4 py-3 font-semibold">GL Code</th>
              <th className="px-4 py-3 font-semibold">Meter Coverage</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground" data-testid="gl-config-empty">
                No GL codes configured yet. Click <span className="font-medium">Add GL Code</span> to set one per utility.
              </td></tr>
            ) : rows.map((r) => {
              const st = stats[r.commodity];
              const isEdit = editing === r.commodity;
              return (
                <tr key={r.commodity} className="border-t border-border" data-testid={`gl-row-${r.commodity}`}>
                  <td className="px-4 py-3">
                    {isEdit ? (
                      <div className="flex flex-col gap-1.5">
                        <Select value={editCommodity} onValueChange={setEditCommodity}>
                          <SelectTrigger className="h-9 w-44 rounded-none" data-testid="gl-edit-utility"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__add__" className="text-primary font-medium" data-testid="gl-edit-add-utility">+ Add utility</SelectItem>
                            <SelectItem value={r.commodity}>{utilityLabel(r.commodity)}</SelectItem>
                            {availableUtilities.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {editCommodity === '__add__' && (
                          <Input value={editCustom} onChange={e => setEditCustom(e.target.value)} className="h-9 w-44 rounded-none" placeholder="New utility name" data-testid="gl-edit-utility-custom" />
                        )}
                      </div>
                    ) : (
                      <span className="font-medium">{utilityLabel(r.commodity)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isEdit ? (
                      <Input value={editGl} onChange={e => setEditGl(e.target.value)} className="h-9 w-40 rounded-none font-mono" data-testid="gl-edit-input" placeholder="e.g. 8200" />
                    ) : (
                      <span className="font-mono">{r.gl_code}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {st ? (
                      <span data-testid={`gl-coverage-${r.commodity}`}>{st.with_gl}/{st.total} meters{st.manual ? ` · ${st.manual} manual` : ''}</span>
                    ) : <span className="text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {isEdit ? (
                        <>
                          <Button size="sm" onClick={() => saveEdit(r.commodity)} disabled={saving} data-testid="gl-save-edit"><Save className="h-3.5 w-3.5 mr-1" /> Save</Button>
                          <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={saving} data-testid="gl-cancel-edit"><X className="h-3.5 w-3.5" /></Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" onClick={() => startEdit(r)} data-testid={`gl-edit-${r.commodity}`} className="rounded-none"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                          <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(r.commodity)} data-testid={`gl-delete-${r.commodity}`} className="text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => { if (!saving) setAddOpen(o); }}>
        <DialogContent className="rounded-none" data-testid="gl-add-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Hash className="h-5 w-5" /> Add GL Code</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Utility</Label>
              <Select value={newCommodity} onValueChange={setNewCommodity}>
                <SelectTrigger className="h-10 mt-1.5 rounded-none" data-testid="gl-add-utility-select"><SelectValue placeholder="Choose a utility" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__add__" className="text-primary font-medium" data-testid="gl-add-utility-option">+ Add utility</SelectItem>
                  {availableUtilities.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {newCommodity === '__add__' && (
                <Input value={newCustom} onChange={e => setNewCustom(e.target.value)} className="h-10 mt-2 rounded-none" placeholder="New utility name (e.g. Recycling)" data-testid="gl-add-utility-custom" />
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">GL Code</Label>
              <Input value={newGl} onChange={e => setNewGl(e.target.value)} className="h-10 mt-1.5 rounded-none font-mono" placeholder="e.g. 8200" data-testid="gl-add-gl-input" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={saving} data-testid="gl-add-cancel">Cancel</Button>
            <Button onClick={handleAdd} disabled={saving} data-testid="gl-add-save">{saving ? 'Saving…' : 'Save GL Code'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent className="rounded-none" data-testid="gl-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove GL code for {utilityLabel(deleteTarget)}?</AlertDialogTitle>
            <AlertDialogDescription>
              This clears the global GL code for this utility. Commodity-sourced meter GL codes will also be cleared (manual per-meter overrides are kept).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="gl-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground" data-testid="gl-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ── Payment File Configuration wrapper: Schedule | Funding | AP File Format ──
const PaymentFileConfigTab = () => {
  const [view, setView] = useState('schedule');
  const btn = (id, active) =>
    `px-5 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 ${
      active ? 'bg-primary text-primary-foreground' : 'bg-card hover:bg-muted'
    }`;
  return (
    <div className="space-y-6" data-testid="payment-file-config-tab">
      <div className="inline-flex border border-border rounded-none overflow-hidden" data-testid="pfc-toggle">
        <button
          onClick={() => setView('schedule')}
          className={btn('schedule', view === 'schedule')}
          data-testid="pfc-toggle-schedule"
        >
          <CalendarClock className="h-4 w-4" /> Payment Schedule
        </button>
        <button
          onClick={() => setView('funding-format')}
          className={`${btn('funding-format', view === 'funding-format')} border-l border-border`}
          data-testid="pfc-toggle-funding-format"
        >
          <Landmark className="h-4 w-4" /> Funding File Format
        </button>
        <button
          onClick={() => setView('ap-format')}
          className={`${btn('ap-format', view === 'ap-format')} border-l border-border`}
          data-testid="pfc-toggle-ap-format"
        >
          <FileSpreadsheet className="h-4 w-4" /> AP File Format
        </button>
      </div>
      {view === 'schedule' && <PaymentsScheduleTab />}
      {view === 'funding-format' && <FundingFileFormatTab />}
      {view === 'ap-format' && <ApFileFormatTab />}
    </div>
  );
};

// ── AP File Format ──
const ApFileFormatTab = () => (
  <FileFormatTab
    basePath="/payment-schedule/ap-format"
    testidPrefix="ap-format"
    title="AP File Format"
    titleIcon={<FileSpreadsheet className="h-5 w-5 text-emerald-600" />}
    savedLabel="AP file"
    intro={
      <>Choose how this organisation&apos;s AP / payment file is laid out when downloaded.
      Pick one of the ready-made formats, or upload your own sample so the file matches your
      accounting system exactly. The chosen format applies to the <strong>Download AP File</strong>
      &nbsp;action on each payment file.</>
    }
  />
);

// ── Funding File Format ──
const FundingFileFormatTab = () => (
  <FileFormatTab
    basePath="/payment-schedule/funding-format"
    testidPrefix="funding-format"
    title="Funding File Format"
    titleIcon={<Landmark className="h-5 w-5 text-indigo-600" />}
    savedLabel="funding file"
    intro={
      <>Choose how this organisation&apos;s <strong>funding file</strong> is laid out. Use the
      <strong> Standard Funding File</strong>, or upload your own sample and let Astrid map its
      columns to BillPortal&apos;s data fields — with confidence scores and clarifying questions
      before you save.</>
    }
  />
);

// ── Generic file-format selector: presets + Customize (upload) + Astrid map ──
const FileFormatTab = ({ basePath, testidPrefix, title, titleIcon, savedLabel, intro }) => {
  const t = testidPrefix;
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);

  const acctId = (() => {
    try { return JSON.parse(localStorage.getItem('current_account') || '{}').id; }
    catch { return undefined; }
  })();
  const params = { account_id: acctId };
  const uploadInputId = `${t}-custom-upload`;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(basePath, { params });
      setCfg(data);
      setSelected(data.format_id);
    } catch {
      toast.error(`Could not load ${savedLabel} format`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.put(basePath, { format_id: selected }, { params });
      setCfg(data);
      setSelected(data.format_id);
      toast.success(`${title} saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to save ${savedLabel} format`);
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith('.csv') && !name.endsWith('.xlsx')) {
      toast.error('Only .csv or .xlsx files are supported');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post(`${basePath}/custom-upload`, form, {
        params, headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCfg(data);
      setSelected('custom');
      toast.success('Custom format uploaded — remember to Save');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const [removing, setRemoving] = useState(false);
  const removeCustom = async () => {
    setRemoving(true);
    try {
      const { data } = await api.delete(`${basePath}/custom`, { params });
      setCfg(data);
      setSelected(data.format_id);
      toast.success('Uploaded file removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove file');
    } finally {
      setRemoving(false);
    }
  };

  const [expanded, setExpanded] = useState({});
  const toggleExpand = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const [downloadingSample, setDownloadingSample] = useState(false);
  const downloadSample = async () => {
    if (!selected) return;
    setDownloadingSample(true);
    try {
      const res = await api.get(`${basePath}/sample`, {
        params: { ...params, format_id: selected }, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t}_sample_${selected}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Sample file downloaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to download sample');
    } finally {
      setDownloadingSample(false);
    }
  };

  if (!cfg) {
    return (
      <div className="border border-border rounded-none p-8 bg-card" data-testid={`${t}-tab`}>
        <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const presets = cfg.presets || [];
  const custom = cfg.custom_template;
  const dirty = selected !== cfg.format_id;

  return (
    <div className="border border-border rounded-none p-8 bg-card space-y-6" data-testid={`${t}-tab`}>
      <div>
        <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          {titleIcon} {title}
        </h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{intro}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {presets.map((p) => {
          const active = selected === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelected(p.id)}
              className={`text-left border rounded-none p-5 transition-colors ${
                active ? 'border-primary ring-1 ring-primary bg-primary/5' : 'border-border hover:border-primary/50'
              }`}
              data-testid={`${t}-card-${p.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <span className="font-semibold">{p.label}</span>
                {active && <Check className="h-4 w-4 text-primary shrink-0" />}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{p.description}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(expanded[p.id] ? (p.columns || []) : (p.columns || []).slice(0, 8)).map((c) => (
                  <span key={c} className="text-[10px] bg-muted px-1.5 py-0.5 rounded-none border border-border">{c}</span>
                ))}
                {(p.columns || []).length > 8 && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); toggleExpand(p.id); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); toggleExpand(p.id); } }}
                    className="text-[10px] text-primary hover:underline px-1 cursor-pointer"
                    data-testid={`${t}-card-${p.id}-more`}
                  >
                    {expanded[p.id] ? 'show less' : `+${p.columns.length - 8} more`}
                  </span>
                )}
              </div>
            </button>
          );
        })}

        {/* Customize card */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => setSelected('custom')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected('custom'); } }}
          className={`text-left border rounded-none p-5 transition-colors cursor-pointer ${
            selected === 'custom' ? 'border-primary ring-1 ring-primary bg-primary/5' : 'border-border hover:border-primary/50'
          }`}
          data-testid={`${t}-card-custom`}
        >
          <div className="flex items-start justify-between gap-3">
            <span
              className="font-semibold flex items-center gap-2"
              data-testid={`${t}-select-custom`}
            >
              <FileCog className="h-4 w-4" /> Customize
            </span>
            {selected === 'custom' && <Check className="h-4 w-4 text-primary shrink-0" />}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Upload a sample <strong>.csv</strong> or <strong>.xlsx</strong>. Its header row defines the
            exact columns and order of your downloaded {savedLabel}.
          </p>

          {custom && (
            <div className="mt-3 text-xs space-y-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <FileSpreadsheet className="h-3.5 w-3.5" />
                <span data-testid={`${t}-custom-filename`} className="font-medium text-foreground">{custom.filename}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(expanded.custom ? (custom.columns || []) : (custom.columns || []).slice(0, 10)).map((c, i) => (
                  <span key={i} className="text-[10px] bg-muted px-1.5 py-0.5 rounded-none border border-border">{c.header}</span>
                ))}
                {(custom.columns || []).length > 10 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleExpand('custom'); }}
                    className="text-[10px] text-primary hover:underline px-1"
                    data-testid={`${t}-card-custom-more`}
                  >
                    {expanded.custom ? 'show less' : `+${custom.columns.length - 10} more`}
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <input
              type="file"
              accept=".csv,.xlsx"
              id={uploadInputId}
              className="hidden"
              onChange={onUpload}
              data-testid={`${t}-upload-input`}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading || removing}
              onClick={(e) => { e.stopPropagation(); document.getElementById(uploadInputId)?.click(); }}
              data-testid={`${t}-upload-btn`}
            >
              {uploading ? <RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-2" />}
              {custom ? 'Replace sample file' : 'Upload sample file'}
            </Button>
            {custom && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                disabled={removing || uploading}
                onClick={(e) => { e.stopPropagation(); removeCustom(); }}
                data-testid={`${t}-remove-custom-btn`}
              >
                {removing ? <RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-2" />}
                Remove file
              </Button>
            )}
          </div>
        </div>
      </div>

      {custom && (
        <AstridApMapper custom={custom} params={params} onConfirmed={load} basePath={basePath} savedLabel={savedLabel} />
      )}

      <div className="flex items-center gap-3 pt-2 border-t border-border">
        <Button
          type="button"
          variant="outline"
          onClick={downloadSample}
          disabled={downloadingSample || !selected}
          data-testid={`${t}-sample-download-btn`}
        >
          {downloadingSample ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Download sample
        </Button>
        <Button onClick={save} disabled={saving || !selected || !dirty} data-testid={`${t}-save-btn`}>
          {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save format
        </Button>
        {!dirty && (
          <span className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid={`${t}-active-label`}>
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
            Active: <strong>{selected === 'custom' ? (custom?.filename || 'Custom') : (presets.find((p) => p.id === selected)?.label || selected)}</strong>
          </span>
        )}
      </div>
    </div>
  );
};
